import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { SurfaceShell } from '../../components/SurfaceShell.tsx';
import { Panel, Section, Eyebrow } from '../../components/Panel.tsx';
import {
  Toolbar,
  ToolbarDivider,
  ToolbarLabel,
  ToolbarSpacer,
  SegmentedControl,
} from '../../components/Toolbar.tsx';
import { CheckBox, SearchInput, Select } from '../../components/Field.tsx';
import { Button } from '../../components/Button.tsx';
import { Chip } from '../../components/Chip.tsx';
import { Distribution, type DistItem } from '../../components/Distribution.tsx';
import { StateLabel } from '../../components/StatusBadge.tsx';
import { StatusDot } from '../../components/StatusDot.tsx';
import { KeyValue, type KVItem } from '../../components/KeyValue.tsx';
import { Empty, ErrorLine } from '../../components/Empty.tsx';
import { Icon } from '../../shell/Icon.tsx';
import { duration, NO_VALUE, relative, utc, utcTime } from '../../components/format.ts';
import { useNow } from '../../components/useNow.ts';
import { notifyOk, notifyError } from '../../components/toastStore.ts';
import { LIVENESS_STATE, type Tone } from '../../components/state.ts';
import { useRobots, type Liveness, type Robot } from '../../hooks/useRobots.ts';
import { usePendingCommands, type CommandStatus } from '../../hooks/useCommands.ts';
import { useFleetCommandStream } from '../../hooks/useCommandStream.ts';
import { CommandPanel, type StageRequest } from '../fleet/views/CommandPanel.tsx';
import { FlowStrip } from './FlowStrip.tsx';
import { LifecycleTrack } from './LifecycleTrack.tsx';
import { TransitionTrace } from './TransitionTrace.tsx';
import { FeedTag, StaleLine } from './FeedTag.tsx';
import { health, source, type Health } from './freshness.ts';
import { useScrollEdges } from './useScrollEdges.ts';
import {
  AUDIT_WINDOW,
  useCancelCommand,
  useCommandAudit,
  useCommandLedger,
  type CommandEvent,
  type LedgerCommand,
} from './useCommandLedger.ts';
import {
  CMD_HINT,
  CMD_STATUSES,
  CMD_TONE,
  IN_FLIGHT,
  LATENCY_BUCKETS,
  TERMINAL,
  type Band,
  type Ladder,
  bandNote,
  bucketOf,
  detailOf,
  failureOf,
  formatParam,
  paramDigest,
  ladderOf,
  readProfile,
  latencyBand,
  latencyMs,
  latencyText,
  percentile,
  waitBand,
  worstBand,
} from './commandModel.ts';
import { BandedAge } from './BandedAge.tsx';
import {
  Blank, Figure, ReadBody, blankLine, figure, unread, useStanding, type Read,
} from '../../components/states/index.ts';
import { RobotLink } from '../../components/AppLink.tsx';
import styles from './CommandsWorkspace.module.css';

/* ==========================================================================
   COMMAND QUEUE — the fleet's C2 board.

   Four panes on one ground, and each one answers a different question an
   operator actually asks:

     FILTERS (left rail)   what is the queue made of, and how fast is it
                           moving? Facets as Gotham's label / count / bar rows
                           (palantir-gotham-facet-rail-timeline.png), plus the
                           dispatch-latency histogram over the same window.

     BOARD (centre top)    what is in flight, grouped by lifecycle state with
                           the count and the oldest wait on every group header
                           and a time-in-state badge on every row
                           (skydio-dfr-incident-dispatch.png). Group by robot,
                           verb or issuer instead, and the per-row lifecycle
                           track becomes the state carrier.

     ACTIVITY (centre bot) what just happened — the raw audit event stream at
                           its true timestamps, newest first.

     RAIL (right)          the selected command in full — its real transition
                           ledger, its params, its failure — over the dispatch
                           control, whose commit pair is pinned to the bottom
                           edge (anduril-lattice-asset-detail-tasking.png).

   Everything on screen comes off `/api/commands/pending`, `/api/audit/commands`
   and `/api/robots`. The one derived quantity is latency, and it is labelled
   as exactly what it is: issue → the state the command is in now.
   ========================================================================== */

type GroupBy = 'state' | 'robot' | 'command' | 'issuer';

const GROUP_ITEMS: readonly { value: GroupBy; label: string }[] = [
  { value: 'state', label: 'State' },
  { value: 'robot', label: 'Robot' },
  { value: 'command', label: 'Verb' },
  { value: 'issuer', label: 'Issuer' },
];

interface Group {
  key: string;
  label: ReactNode;
  hint: string;
  rows: LedgerCommand[];
  tone: Tone | null;
  /** Failures inside the group. Zero when the group IS a lifecycle state. */
  fails: number;
  /**
   * The lifecycle state this group IS, where it is one.
   *
   * Non-null only under the `state` grouping, and it is what lets the header
   * carry the track the rows no longer draw. Under the other three groupings a
   * group holds a mix of states, no single track describes it, and the column
   * stays on the rows where it belongs.
   */
  status: CommandStatus | null;
}

/**
 * The facet bar's length, in px.
 *
 * Distribution's own default is the 176px `--w-dist` ruler, which in a 232px
 * rail leaves 48px for the label — enough for `stop` and not for
 * `start_capture`. 64px leaves the label column 96px, which is the longest
 * verb in the profile set at 11px mono, so no category name is ever cut.
 */
const FACET_BAR = 64;

/**
 * How many rows of the two open-ended facets are on screen before the tail is
 * folded.
 *
 * Both lists are sorted by count, so the head is the part that carries the
 * shape; the tail is a long thin drizzle of ones. Unfolded they are 21 rows
 * between them, which at a laptop height pushed the latency profile — and
 * most of the issuer list with it — off the bottom of a 232px rail. Folded,
 * the whole rail fits at 900px and the tail is one click (or the search box)
 * away. The active facet is never folded away, whatever its rank.
 */
const VERB_CAP = 8;
const ISSUER_CAP = 5;

/** Groups the board opens closed. Terminal history is context, not work. */
const DEFAULT_CLOSED: readonly string[] = ['completed', 'cancelled'];

/* --------------------------------------------------------------------------
   Column sort

   Nine of the columns carry a scalar an operator triages on; the rest (the
   state edge, the stage track and the detail) do not order meaningfully, so
   they are labels and not controls. Sorting happens INSIDE the grouping —
   picking `In state` on a state-grouped board reorders each lifecycle group by
   how long its rows have been sitting there, which is the question, rather
   than dissolving the grouping that framed it.
   -------------------------------------------------------------------------- */

type SortKey =
  | 'id' | 'robot' | 'command' | 'issued' | 'latency' | 'instate'
  | 'build' | 'link' | 'site';

interface Sort {
  key: SortKey;
  dir: 'asc' | 'desc';
}

/** Worst first. A queue's reachability column is opened to find the machines
    a command can never reach, not to admire the live ones. */
const LINK_RANK: Record<Liveness, number> = {
  offline: 0, provisioning: 1, stale: 2, backfilling: 3, live: 4,
};

/** The site a robot is standing in, off `/api/robots`. Never guessed: a robot
    whose registry row carries no site reads as the em dash. */
function siteOf(r: Robot | null): string | null {
  const v = r?.meta['site'];
  return typeof v === 'string' && v !== '' ? v : null;
}

/** The build the target published — `firmware`, which the command profile
    republishes as `platform_version`. One string, one name for it. */
function buildOf(r: Robot | null): string | null {
  return r?.firmware ?? null;
}

/** A comparator per key. Ascending; the caller flips the sign.

    Latency is null for anything still in flight — nothing has settled, so
    there is no measurement, and a missing measurement sorts to the END in
    both directions rather than being read as zero. That is the same rule the
    column itself follows when it renders an em dash.

    The three TARGET keys are resolved through the registry index rather than
    off the command, because they are facts about the machine and not about
    the row — which is also why their headers say `now`. */
function comparator(
  key: SortKey,
  idx: ReadonlyMap<string, Robot>,
): (a: LedgerCommand, b: LedgerCommand) => number {
  const robotOf = (c: LedgerCommand) => idx.get(c.robot_id) ?? null;
  switch (key) {
    case 'id':
      return (a, b) => a.id - b.id;
    case 'robot':
      return (a, b) => a.robot_id.localeCompare(b.robot_id);
    case 'command':
      return (a, b) => a.command.localeCompare(b.command);
    case 'issued':
      return (a, b) => Date.parse(a.issued_at) - Date.parse(b.issued_at);
    case 'latency':
      return (a, b) => {
        const x = latencyMs(a);
        const y = latencyMs(b);
        if (x === null && y === null) return 0;
        if (x === null) return 1;
        if (y === null) return -1;
        return x - y;
      };
    case 'build':
      /* Version strings, compared numerically per segment so 2.10.7 sorts
         after 2.9.4 — which a plain string compare gets backwards, and a
         fleet-wide version column that lies about ordering is worse than no
         column. A robot the registry has not answered for sorts last. */
      return (a, b) => cmpVersion(buildOf(robotOf(a)), buildOf(robotOf(b)));
    case 'link':
      return (a, b) => {
        const x = robotOf(a)?.liveness;
        const y = robotOf(b)?.liveness;
        if (x === undefined && y === undefined) return 0;
        if (x === undefined) return 1;
        if (y === undefined) return -1;
        return LINK_RANK[x] - LINK_RANK[y];
      };
    case 'site':
      return (a, b) => {
        const x = siteOf(robotOf(a));
        const y = siteOf(robotOf(b));
        if (x === null && y === null) return 0;
        if (x === null) return 1;
        if (y === null) return -1;
        return x.localeCompare(y);
      };
    /* Time in state is `now − updated_at`, and `now` is the same instant for
       every row, so ordering by it is ordering by `updated_at` — computed
       without the clock, which keeps the sort from churning on every tick. */
    case 'instate':
      return (a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at);
  }
}

/** `2.9.4` before `2.10.7`. Segment-wise, numeric where both segments are
    numeric, lexical otherwise (so `3.0.0-rc2` orders under `3.0.0`). */
function cmpVersion(a: string | null, b: string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const xs = a.split(/[.-]/);
  const ys = b.split(/[.-]/);
  for (let i = 0; i < Math.max(xs.length, ys.length); i += 1) {
    const x = xs[i];
    const y = ys[i];
    if (x === undefined) return -1;
    if (y === undefined) return 1;
    const nx = Number(x);
    const ny = Number(y);
    if (Number.isFinite(nx) && Number.isFinite(ny)) {
      if (nx !== ny) return nx - ny;
    } else if (x !== y) {
      return x.localeCompare(y);
    }
  }
  return 0;
}

/** Which way a column reads first. A name reads A→Z; a duration and an id
    read biggest-first, because the reason to sort by them is to find the
    extreme; a link reads worst-first for the same reason. */
const FIRST_DIR: Record<SortKey, 'asc' | 'desc'> = {
  id: 'desc',
  robot: 'asc',
  command: 'asc',
  issued: 'desc',
  latency: 'desc',
  instate: 'desc',
  build: 'asc',
  link: 'asc',
  site: 'asc',
};

/* ==========================================================================
   THE COLUMN MODEL — one place, measured, shared by the head and the rows.

   WHY THIS IS IN TYPESCRIPT AND NOT IN FOUR MEDIA QUERIES.

   It used to be four hand-written `grid-template-columns` in the stylesheet,
   one per breakpoint, and the row markup emitted every cell at every width
   with `display: none` doing the shedding. That has two failures the board
   was measurably guilty of:

     · ADDING WIDTH ADDED HOLE, NOT INFORMATION. One greedy `Detail` column at
       `3fr` opened a 270px ink-free corridor at 1920 across every in-flight
       row — and an in-flight row is the top of the board, which is where the
       operator is looking. Surplus has to be spread over the columns that
       actually carry ink, and past a certain width it has to buy a NEW column
       rather than a wider one.
     · THE GROUPING KEY WAS REPEATED IN EVERY ROW UNDER IT. Grouping by verb
       printed `return_home` on all sixteen rows beneath a `return_home 16`
       header. The column the grouping already names is not information, and
       dimming it (what this did before) still spends its width.

   So the set is computed: the ordered list, minus whatever the grouping
   already says, shed from the tail of a priority list until the minima fit,
   and the surplus divided by each column's own `grow`. The head and the rows
   read the same array, so they cannot disagree about a column.
   ========================================================================== */

type ColKey =
  | 'edge' | 'pick' | 'id' | 'robot' | 'site' | 'build' | 'link' | 'verb'
  | 'stage' | 'detail' | 'issuer' | 'issued' | 'latency' | 'instate';

interface ColDef {
  /** Narrowest the column is allowed to be, px. */
  min: number;
  /** Share of the surplus. 0 = fixed. */
  grow: number;
}

const BOARD_COLS: Record<ColKey, ColDef> = {
  edge: { min: 2, grow: 0 },
  /* The selection gutter. 20px is the shared `CheckBox`'s own 14px face plus
     the 3px either side that keeps its hit target at 20 — the same cell width
     `DataTable .selCell` reserves, so a checkbox on this board and a checkbox
     on the fleet table sit at the same distance from the row's edge. */
  pick: { min: 20, grow: 0 },
  /* Minima are measured ink at 11px mono (6.623px/char) plus the cell insets,
     not round numbers: `#110` is 4 characters, `jetrover-01` 11, `3.0.0-rc2`
     9, `backfilling` 11 behind a 6px dot, `start_mapping` 13. Every one of
     them was carrying 6–14px of slack it could not use, and the sum of that
     slack was one whole column — at 1600 the board was 12px short of holding
     `Issued` and shed it. */
  id: { min: 38, grow: 0.3 },
  robot: { min: 88, grow: 1.2 },
  site: { min: 112, grow: 0.7 },
  build: { min: 66, grow: 0.2 },
  link: { min: 86, grow: 0.4 },
  verb: { min: 98, grow: 1.2 },
  stage: { min: 58, grow: 0 },
  /* DETAIL TAKES THE SURPLUS, BUT NOT ALL OF IT. At `3fr` against a combined
     6.4 elsewhere it was collecting a third of every pixel the board gained,
     and on the in-flight rows at the top of the board — the rows an operator
     is actually looking at — a pending `pause` has no parameters and no result
     to print, so the third was a right margin 370px wide at 1920. It is still
     the widest column and still the only one that can hold a sentence; it is
     no longer the column that eats the room. */
  detail: { min: 152, grow: 2.2 },
  issuer: { min: 124, grow: 1.2 },
  issued: { min: 84, grow: 0 },
  latency: { min: 64, grow: 0.6 },
  instate: { min: 64, grow: 0.6 },
};

/* DETAIL IS LAST, AND SO IS THE LOG'S PAYLOAD. One rule, both tables.

   A free-text column in the MIDDLE of a wide table is a corridor generator:
   it is empty on every in-flight row — which is the top of the board, where
   the operator is looking — and the slack shows up as an ink-free band
   between two inked columns, which reads as a broken render. Put it last and
   the same slack is a ragged right margin, which reads as "this command has
   no detail". It also means the one column that can carry a sentence is the
   one that gets the surplus, so `NODE_DOWN capture node stopped responding`
   is legible instead of clipped at `NODE_DOWN capture no…`. */
const BOARD_ORDER: readonly ColKey[] = [
  'edge', 'pick', 'id', 'robot', 'site', 'build', 'link', 'verb', 'stage',
  'issuer', 'issued', 'latency', 'instate', 'detail',
];

/** Shed order — least load-bearing first. Everything dropped here is still on
    the record pane one click away, and the board never buys a horizontal
    scrollbar. `site` goes first because the robot id is already on the row and
    a site is a property of the machine rather than of the dispatch; the
    absolute issue time goes next because its relative age is still on the row.

    `build` IS THE LAST THING TO GO, AFTER `latency`. §13 tell 23 asks whether
    the agent version is a column at fleet scale, and at 1280 — where the board
    has 664px and can hold six columns — the answer has to stay yes: a run of
    one fault code against one build is a fleet regression and it is only
    visible as a column. `Latency` goes first of the two because it is issue →
    now, which is the em dash on every pending row, while `In state` (THE BAR
    §13 tell 19 — a queue has to show time IN state) is a reading on all six
    statuses and stays to the last. */
const BOARD_SHED: readonly ColKey[] = ['site', 'issued', 'link', 'issuer', 'latency', 'build'];

/** Which column the grouping already names, and therefore must not repeat.

    `state` DROPS THE TRACK, NOT NOTHING. `LifecycleTrack` is a pure function of
    `status` — the same four pips for every command in a lifecycle group — so
    grouping by state printed one identical 58px glyph on all 107 rows under
    headers that already said `PENDING` / `SENT` / `ACKED`. That is the
    grouping-key redundancy this table was built to refuse, in its purest form:
    64px x 107 of a column carrying nothing the header above it had not already
    said. So the track moves INTO the state group's header, where it is drawn
    once and still answers "how far does this state mean the command got",
    and the width it was spending goes to `Detail`.

    Under the other three groupings the states inside a group are mixed and the
    track is the row's own state carrier, so it stays on the row. */
const GROUP_COL: Record<GroupBy, ColKey | null> = {
  state: 'stage',
  robot: 'robot',
  command: 'verb',
  issuer: 'issuer',
};

const COL_GAP = 6;

/**
 * The set of columns that fits `width`, in drawing order.
 *
 * `width <= 0` means nothing has been measured yet — the observer has not
 * fired — and the answer is the narrow set, so the first paint is never wider
 * than the panel it lands in.
 */
function fitColumns<K extends string>(
  order: readonly K[],
  defs: Record<K, ColDef>,
  shed: readonly K[],
  width: number,
  drop: K | null,
): K[] {
  let cols = order.filter((k) => k !== drop);
  const total = (list: readonly K[]) =>
    list.reduce((n, k) => n + defs[k].min, 0) + (list.length - 1) * COL_GAP + 12;
  const room = width > 0 ? width : 0;
  for (const k of shed) {
    if (total(cols) <= room) break;
    cols = cols.filter((c) => c !== k);
  }
  return cols;
}

/* --------------------------------------------------------------------------
   HOW MUCH OF THE SURPLUS THE FREE-TEXT COLUMN IS WORTH, MEASURED.

   `Detail` and `Payload` are the two columns whose content the platform
   supplies only sometimes: a settled command has a result or a fault, a
   `pending pause` has neither, and a `sent` transition carries no body at all.
   Weighting them the same in both cases is how the board ends up spending a
   third of every pixel it gains on a column of em dashes — the exact failure
   ("adding width adds hole, not information") the column model was rebuilt to
   remove, displaced from the middle of the table to its right-hand end.

   So the weight is a function of the rows actually on screen. Quantised to
   three steps, deliberately: a continuous ratio would re-flow the column by a
   pixel or two on every 3s poll as one command settles, and a table whose
   columns twitch is worse than one that is slightly too wide. Filtering to
   `status failed` — where every row has a fault sentence — moves it to the
   top step; filtering to `sent`, where none has anything, to the bottom.
   -------------------------------------------------------------------------- */
function textGrow(withInk: number, total: number): number {
  if (total === 0) return 1;
  const fill = withInk / total;
  return fill >= 0.6 ? 2.4 : fill >= 0.25 ? 1.6 : 0.9;
}

/** The grid template for a fitted set: fixed columns at their minimum, the
    rest `minmax(min, grow fr)` so the surplus lands in proportion. */
function gridTemplate<K extends string>(cols: readonly K[], defs: Record<K, ColDef>): string {
  return cols
    .map((k) => {
      const d = defs[k];
      return d.grow === 0 ? `${d.min}px` : `minmax(${d.min}px, ${d.grow}fr)`;
    })
    .join(' ');
}

/* --------------------------------------------------------------------------
   The activity log's own set. Same machinery, different columns.

   `payload` is the log's free-text column and it is LAST, left-aligned, which
   is the whole reason the log no longer has a corridor down its middle: a
   short message leaves a ragged right margin, which reads as a short message,
   where the same slack between two columns reads as a hole. Every column
   before it either always carries ink or is a fixed-width instrument.
   -------------------------------------------------------------------------- */

type FeedKey =
  | 'time' | 'age' | 'delta' | 'id' | 'robot' | 'build' | 'event'
  | 'verb' | 'who' | 'payload';

/* Minima are the column's own widest ink at the row's type size plus the cell
   insets, and the row is now 11px mono like the board's (a 10px log under an
   11px queue was two type sizes for one kind of string). 11px advances
   6.623px against 10px's 6.021 — measured, not estimated — so every text
   minimum here went up by that ratio and no more. */
const FEED_COLS: Record<FeedKey, ColDef> = {
  time: { min: 78, grow: 0 },
  age: { min: 58, grow: 0 },
  delta: { min: 66, grow: 0 },
  id: { min: 42, grow: 0 },
  robot: { min: 100, grow: 0.8 },
  build: { min: 70, grow: 0 },
  event: { min: 94, grow: 0 },
  verb: { min: 108, grow: 0.8 },
  who: { min: 132, grow: 1 },
  /* THE FREE-TEXT COLUMN'S MINIMUM IS WHAT IT COSTS THE COLUMN BESIDE IT.
     At 148 the log gave up `Verb` at 1280 to hold 22 characters of a payload
     that ellipsises at every width anyway — a free-text floor that evicts a
     named enum is the wrong trade. 108 is 16 characters, enough for
     `duration_s 41.6` whole, and it keeps the verb on the row down to 1280;
     the column still takes the largest share of every pixel above that. */
  payload: { min: 108, grow: 1.1 },
};

/* NO `SINCE ISSUE` COLUMN, DELIBERATELY.

   It was here, next to `Δ prev`, and on this platform the two print the same
   number: a command's audit trail is `issued` plus ONE transition on 200 of
   the ledger's 211 events, so `issue → this hop` and `previous hop → this hop`
   are the same measurement with two headings. That is the grouping-key
   redundancy in another costume. The per-hop delta is the one that keeps
   meaning something when a command does go issued → sent → acked → completed,
   so it is the one that stays. */
const FEED_ORDER: readonly FeedKey[] = [
  'time', 'age', 'delta', 'id', 'robot', 'build', 'event', 'verb',
  'who', 'payload',
];

/* `Age` is how long ago the transition happened; `Δ prev` is what the hop
   cost. The second is the measurement, so it is the last duration to go. */
const FEED_SHED: readonly FeedKey[] = ['build', 'who', 'age', 'verb', 'delta'];

/**
 * WHAT A TRANSITION ACTUALLY CARRIED, verbatim, for the log's last column.
 *
 * The audit log is the one place on this surface where the raw body belongs:
 * the board's `Detail` column summarises a COMMAND, this summarises an EVENT,
 * and the two disagree on purpose — a `completed` row on the board says
 * `frames 4820` for the whole command, while the log says it against the
 * transition that reported it, at its own timestamp.
 *
 * Nothing is invented. A hop the platform records no body for renders as
 * nothing at all, and the cell says so with the same em dash every other
 * missing value on this console uses.
 */
function payloadOf(ev: CommandEvent): { code: string | null; text: string } {
  const fail = failureOf(ev.result);
  /* The fault's class takes the red; the sentence stays dim. Same split the
     board's Detail column makes, and for the same reason. */
  if (fail !== null) return { code: fail.code, text: fail.message };
  if (ev.event === 'issued') {
    const raw = ev.fields['params'];
    const params = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
    return { code: null, text: paramDigest(params) };
  }
  if (ev.result === null) return { code: null, text: '' };
  /* `ok: true` on a completed body is the event name repeated — the row
     already says `completed`. What is left is what the run produced. */
  const parts = Object.entries(ev.result)
    .filter(([k]) => k !== 'ok')
    .map(([k, v]) => `${k} ${formatParam(k, v)}`);
  if (parts.length > 0) return { code: null, text: parts.join(' · ') };
  const ok = ev.result['ok'];
  return { code: null, text: ok === true ? 'ok' : ok === false ? 'not ok' : '' };
}

/** One column header for the activity log. */
function FeedHead({ col }: { col: FeedKey }) {
  switch (col) {
    case 'time':
      return <span title="When the platform recorded the transition, UTC">Time</span>;
    case 'age':
      return <span className={styles.hRight} title="How long ago it happened">Age</span>;
    case 'delta':
      return (
        <span
          className={styles.hRight}
          title="Time since this command's PREVIOUS transition — the cost of this one hop, not of the whole command. The issue event has no predecessor and reads as the em dash."
        >
          Δ prev
        </span>
      );
    case 'id':
      return <span className={styles.hRight} title="The command this transition belongs to">#</span>;
    case 'robot':
      return <span>Robot</span>;
    case 'build':
      return (
        <span title="The build the target is running now — the same string the queue's Build column carries. A run of one fault code against one version is a fleet regression, and that pattern is only visible as a column.">
          Build
        </span>
      );
    case 'event':
      return <span title="The transition the platform recorded">Event</span>;
    case 'verb':
      return <span title="The verb, joined from the command — a transition does not carry it">Verb</span>;
    case 'who':
      return <span title="Who caused the transition. `system` is the platform's own dispatcher moving the command along.">By</span>;
    case 'payload':
      return (
        <span
          className={styles.headDetail}
          title="The body the transition carried, verbatim: the result of a settled hop, the parameters of an issue, and nothing for a hop the platform records no body for."
        >
          Payload
        </span>
      );
  }
}

/**
 * The width of an element, in px, kept current by a ResizeObserver.
 *
 * The column set is a function of the PANEL's width, not the window's — the
 * facet rail comes and goes at 1200 and the dispatch rail never narrows, so
 * the middle column's width and the viewport's width are two different
 * numbers and a media query is measuring the wrong one. Zero until the
 * observer has fired, which `fitColumns` reads as "take the narrow set".
 */
function useElementWidth<T extends HTMLElement>(): {
  ref: (node: T | null) => void;
  width: number;
} {
  const [node, setNode] = useState<T | null>(null);
  const [width, setWidth] = useState(0);
  const ref = useCallback((n: T | null) => setNode(n), []);
  useEffect(() => {
    if (node === null) return undefined;
    const measure = () => setWidth(node.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, [node]);
  return { ref, width };
}

export function CommandsWorkspace() {
  const ledger = useCommandLedger();
  const { connected } = useFleetCommandStream();
  const { data: robots } = useRobots();
  const cancel = useCancelCommand();
  /* The batch withdraw runs on its own mutation so that the record pane's
     `busy` state stays about the record pane. One shared mutation would grey
     the inspector's own Withdraw while a batch it has nothing to do with is in
     flight. */
  const batchCancel = useCancelCommand();
  const now = useNow();

  /* ---- per-panel freshness -----------------------------------------------
     Both reads again, by the same query key: React Query dedupes them, so
     this costs no request and gives the panels the three facts the ledger's
     folded rows throw away — when each endpoint last SUCCEEDED, whether its
     last attempt failed, and whether anything is being attempted at all.

     They are split, not fused, because the two panels genuinely have
     different answers: `/api/audit/commands` dying leaves the in-flight
     queue current, and the board must say so rather than declaring the whole
     surface dead. §13 tell 27 — one light for four channels is a defect. */
  const auditQ = useCommandAudit();
  const pendingQ = usePendingCommands();

  const auditSrc = source('/api/audit/commands', 3_000, auditQ);
  const pendingSrc = source('/api/commands/pending', 3_000, pendingQ);
  const auditH = health([auditSrc], now);
  const boardH = health([pendingSrc, auditSrc], now);

  /* EVERY FIGURE ON THIS BOARD IS COUNTED ACROSS TWO ENDPOINTS, so the test
     for "is this a measurement" has to be too: `/api/audit/commands` and
     `/api/commands/pending` are folded into one row set, and a figure derived
     from them is real the moment EITHER has answered. `unread` takes the array
     and asks "has not one of these landed" — which is what makes `Queue (—)`
     appear on a cold start and disappear the instant the queue arrives, even
     if the history endpoint is still down. */
  const boardSources = useMemo(
    () => [ledger.read, ledger.pending] as const,
    [ledger.read, ledger.pending],
  );
  const boardBlind = unread(boardSources);

  /* The clocks. `now` ticks and belongs to the freshness chips themselves —
     "Live off 17s" MUST keep counting, that is the whole point of it. Every
     age drawn from a ROW is measured against its own panel's `asOf`, which
     stops the instant that panel's read stops, so a frozen queue cannot show
     an in-state age that keeps growing as though the robot were still there. */
  const boardNow = boardH.asOf;
  const feedNow = auditH.asOf;

  const [query, setQuery] = useState('');
  const [robotFilter, setRobotFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [verbFilter, setVerbFilter] = useState<string | null>(null);
  const [issuerFilter, setIssuerFilter] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<GroupBy>('state');
  /* Null is the ledger's own order — newest issue first — which is what an
     operator wants when they are watching the board fill. It stops being what
     they want the moment they are triaging: then the question is which
     command has been waiting longest, and that row is somewhere in the middle
     of an id-ordered group. So the columns sort, in place, inside the
     grouping (THE BAR §5: sort arrow inline after the label). */
  const [sort, setSort] = useState<Sort | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [target, setTarget] = useState<string | null>(null);
  const [closed, setClosed] = useState<ReadonlySet<string>>(() => new Set(DEFAULT_CLOSED));
  const [confirmCancel, setConfirmCancel] = useState(false);
  /* ---- THE BATCH ---------------------------------------------------------
     A C2 board is a triage surface before it is a reading surface: on a
     fixture with fourteen failures and twenty-four commands outstanding, the
     act an operator performs is "withdraw these five", not "withdraw this one,
     five times". THE BAR §5 names the mechanism exactly — a checkbox column at
     the far left, selected rows tinted with the accent, and the header row
     REPLACED by verbs that each carry their own count — and `DataTable` already
     ships it for every other table on this console. This board is the one
     surface whose entire content is a list and it was the only one without it.

     The batch is a set of ids rather than of rows, so a poll that replaces the
     ledger's array under it does not drop the selection. */
  const [picked, setPicked] = useState<ReadonlySet<number>>(() => new Set<number>());
  /* Where a shift-click measures its run from — the last row whose tick was
     touched, exactly as every list in the category behaves. */
  const anchor = useRef<number | null>(null);
  /* Withdrawing a batch is a two-press act, like every other destructive verb
     on this console: the first press arms the bar and names the count, the
     second sends. There is no dialog — the bar IS the confirmation. */
  const [batchArmed, setBatchArmed] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  /* Which verb the dispatch bench currently has staged. Mirrored here because
     it decides which half of the rail is on screen: a staged command is an
     act in progress and the rail belongs to it until it is sent or stood
     down. */
  const [stagedVerb, setStagedVerb] = useState<string | null>(null);
  /* WHICH PANE THE RAIL IS SHOWING.

     The record and the dispatch bench each need ~430px of a 706px rail, and
     stacking them meant neither got it: the record's viewport measured 158px
     against 430px of content, so 63% of the thing an operator had just
     clicked on was behind a mask — including, on a failed command, the
     FAILURE line itself. Two panes that each need more than half a rail do
     not share a rail. They take turns in it, and the header says whose turn
     it is.

     The switch is not a mode the operator has to remember to set: picking a
     row shows that row's record, staging a verb (by click, by number key, or
     by RE-ISSUE) shows the bench, and a send shows the record of what was
     just sent. */
  const [railTab, setRailTab] = useState<RailTab>('record');
  /* A command the record pane has asked the dispatch rail to put back on the
     bench. Monotonic, so re-issuing the same row twice stages it twice. */
  const [restage, setRestage] = useState<StageRequest | null>(null);
  const boardRef = useRef<HTMLDivElement | null>(null);
  /* The board and the log each measure THEMSELVES and pick the column set
     that fits — see the note on the column model above. */
  const { ref: boardWidthRef, width: boardW } = useElementWidth<HTMLDivElement>();
  const { ref: feedWidthRef, width: feedW } = useElementWidth<HTMLDivElement>();
  const { ref: facetRef, edge: facetEdge } = useScrollEdges<HTMLDivElement>();
  /* Which of the two long facets are showing their whole tail. */
  const [openFacets, setOpenFacets] = useState<ReadonlySet<string>>(() => new Set<string>());
  const toggleFacet = useCallback((key: string) => {
    setOpenFacets((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  /* One click sorts the column the way it reads first; a second flips it; a
     third puts the board back on the ledger's own order, so there is always a
     way home that does not need a reset button. */
  const onSort = useCallback((key: SortKey) => {
    setSort((prev) => {
      if (prev === null || prev.key !== key) return { key, dir: FIRST_DIR[key] };
      if (prev.dir === FIRST_DIR[key]) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      return null;
    });
  }, []);

  const all = ledger.commands;

  /* THE REGISTRY, INDEXED. The board's three TARGET columns — site, build,
     link — are facts about the machine, and the machine's row is in
     /api/robots, which this surface already reads for its robot picker. One
     map, so a 107-row board does not run 107 linear scans per paint. */
  const robotIndex = useMemo(() => {
    const m = new Map<string, Robot>();
    for (const r of robots ?? []) m.set(r.robot_id, r);
    return m;
  }, [robots]);

  /* ---- filtering ---------------------------------------------------------
     Each facet's counts are computed against every OTHER filter, which is
     what makes a facet rail navigable: the number beside `failed` is how many
     rows picking it would give you, not how many exist in the abstract. */
  const matches = useCallback(
    (c: LedgerCommand, skip: 'status' | 'command' | 'issuer' | null): boolean => {
      if (robotFilter !== '' && c.robot_id !== robotFilter) return false;
      if (skip !== 'status' && statusFilter !== null && c.status !== statusFilter) return false;
      if (skip !== 'command' && verbFilter !== null && c.command !== verbFilter) return false;
      if (skip !== 'issuer' && issuerFilter !== null && c.issued_by !== issuerFilter) return false;
      if (query !== '') {
        const q = query.toLowerCase();
        const hay = `${c.id} ${c.robot_id} ${c.command} ${c.issued_by} ${c.status}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    },
    [robotFilter, statusFilter, verbFilter, issuerFilter, query],
  );

  const rows = useMemo(() => {
    const out = all.filter((c) => matches(c, null));
    if (sort === null) return out;
    const cmp = comparator(sort.key, robotIndex);
    const sign = sort.dir === 'asc' ? 1 : -1;
    /* Copied before sorting — `all` is the query cache's own array. The id
       tiebreak keeps the order stable and reproducible across re-reads, so a
       row does not swap places with its neighbour every 3s. */
    return [...out].sort((a, b) => cmp(a, b) * sign || b.id - a.id);
  }, [all, matches, sort, robotIndex]);

  const activeFilters = [
    robotFilter === '' ? null : `robot ${robotFilter}`,
    statusFilter === null ? null : `status ${statusFilter}`,
    verbFilter === null ? null : `verb ${verbFilter}`,
    issuerFilter === null ? null : `issuer ${issuerFilter}`,
    query === '' ? null : `“${query}”`,
  ].filter((s): s is string => s !== null);

  const clearFilters = useCallback(() => {
    setQuery('');
    setRobotFilter('');
    setStatusFilter(null);
    setVerbFilter(null);
    setIssuerFilter(null);
  }, []);

  /* ---- facets ------------------------------------------------------------ */

  const statusFacets = useMemo<DistItem[]>(() => {
    const pool = all.filter((c) => matches(c, 'status'));
    const blind = unread(boardSources);
    return CMD_STATUSES.map((s) => ({
      key: s,
      label: <StateLabel tone={CMD_TONE[s]} label={s} />,
      /* `Distribution` prints `null` as `—` and documents why: "a category we
         have no count for is not zero". Six statuses each reading `0` off an
         unread ledger is six categories we have no count for. */
      count: blind ? null : pool.filter((c) => c.status === s).length,
      tone: CMD_TONE[s],
      title: CMD_HINT[s],
    }));
  }, [all, matches, boardSources]);

  const verbFacets = useMemo<DistItem[]>(() => {
    const pool = all.filter((c) => matches(c, 'command'));
    const counts = new Map<string, number>();
    for (const c of pool) counts.set(c.command, (counts.get(c.command) ?? 0) + 1);
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([verb, n]) => ({
        key: verb,
        label: <span className={styles.facetMono}>{verb}</span>,
        count: n,
      }));
  }, [all, matches]);

  const issuerFacets = useMemo<DistItem[]>(() => {
    const pool = all.filter((c) => matches(c, 'issuer'));
    const counts = new Map<string, number>();
    for (const c of pool) counts.set(c.issued_by, (counts.get(c.issued_by) ?? 0) + 1);
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([who, n]) => ({
        key: who,
        label: <span className={styles.facetMono}>{who}</span>,
        count: n,
        title: who,
      }));
  }, [all, matches]);

  /* ---- latency -----------------------------------------------------------
     ISSUE → SETTLE, over the commands that actually settled.

     ONE POPULATION, ONE PAIR OF NUMBERS, EVERYWHERE ON THIS SURFACE. This
     used to sample every command with two timestamps, which folded three
     different quantities — issue→sent, issue→ack and issue→settle — into one
     distribution, and left the board printing `p50 2m 42s` in its header
     while the flow plot beneath it drew its dashed p50 rule at 2m 50s. Two
     numbers with the same name and different values is a surface an operator
     cannot reason about. The complete measurement is the one the platform can
     actually stand behind: a command that finished, and how long it took.

     A command still in flight contributes no sample — nothing has finished,
     and counting its elapsed-so-far as a completion would be a lie that
     flatters the platform. Its elapsed IS still drawn, in its own row, and
     inked against this distribution: "already past what 95% of commands take
     to finish" is exactly the operator's question. */
  const latency = useMemo(() => {
    const samples: number[] = [];
    const buckets = new Map<string, number>();
    for (const c of rows) {
      if (!TERMINAL.has(c.status)) continue;
      const ms = latencyMs(c);
      if (ms === null) continue;
      samples.push(ms);
      const b = bucketOf(ms);
      buckets.set(b, (buckets.get(b) ?? 0) + 1);
    }
    samples.sort((a, b) => a - b);
    return {
      n: samples.length,
      p50: percentile(samples, 0.5),
      p95: percentile(samples, 0.95),
      /* The same two numbers, packaged as the ladder every duration cell on
         this surface is inked against. The board measures its own window and
         then USES what it measured — a p95 that is printed three times and
         obeyed nowhere is decoration. */
      ladder: ladderOf(samples),
      items: LATENCY_BUCKETS.map<DistItem>((b) => ({
        key: b.key,
        label: <span className={styles.facetMono}>{b.label}</span>,
        count: buckets.get(b.key) ?? 0,
        /* The tail bucket is the latency column's own ceiling: a bar here and
           a red cell on the board are the same claim about the same command. */
        ...(b.max === Number.POSITIVE_INFINITY ? { tone: 'error' as Tone } : {}),
      })),
    };
  }, [rows]);

  const ladder = latency.ladder;

  /* ---- the column sets ---------------------------------------------------
     Measured off the two panels themselves, and stripped of whatever the
     grouping already names. */
  const boardCols = useMemo(
    () => fitColumns(BOARD_ORDER, BOARD_COLS, BOARD_SHED, boardW, GROUP_COL[groupBy]),
    [boardW, groupBy],
  );
  const feedCols = useMemo(
    () => fitColumns(FEED_ORDER, FEED_COLS, FEED_SHED, feedW, null),
    [feedW],
  );

  /* ---- grouping ---------------------------------------------------------- */

  const groups = useMemo<Group[]>(() => {
    if (groupBy === 'state') {
      return CMD_STATUSES.map((s) => ({
        key: s,
        label: <span className={styles.groupWord}>{s}</span>,
        hint: CMD_HINT[s],
        rows: rows.filter((c) => c.status === s),
        tone: CMD_TONE[s],
        fails: 0,
        status: s,
      })).filter((g) => g.rows.length > 0);
    }
    const keyOf = (c: LedgerCommand) =>
      groupBy === 'robot' ? c.robot_id : groupBy === 'command' ? c.command : c.issued_by;
    const map = new Map<string, LedgerCommand[]>();
    for (const c of rows) {
      const k = keyOf(c);
      const list = map.get(k);
      if (list === undefined) map.set(k, [c]);
      else list.push(c);
    }
    return [...map.entries()]
      .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
      .map(([k, list]) => ({
        key: k,
        label: <span className={styles.groupMono}>{k}</span>,
        hint: `${list.length} commands`,
        rows: list,
        tone: list.some((c) => c.status === 'failed')
          ? ('error' as Tone)
          : list.some((c) => IN_FLIGHT.has(c.status))
            ? ('nominal' as Tone)
            : null,
        fails: list.filter((c) => c.status === 'failed').length,
        status: null,
      }));
  }, [rows, groupBy]);

  /* Groups open by default whenever the operator is not looking at the
     lifecycle grouping — "koko-04" collapsed is a robot you cannot see. */
  const isClosed = useCallback(
    (key: string) => (groupBy === 'state' ? closed.has(key) : false),
    [groupBy, closed],
  );

  /* Filtering re-opens everything. `completed` and `cancelled` start closed
     because unfiltered they are yesterday's work — but the moment an operator
     narrows to `verb cut` or one robot, a collapsed group is a match they
     cannot see, and the board reads as empty when it is not. Leaving the
     filter puts the default back. */
  const anyFilter = activeFilters.length > 0;
  useEffect(() => {
    setClosed(anyFilter ? new Set() : new Set(DEFAULT_CLOSED));
  }, [anyFilter]);

  const toggleGroup = useCallback((key: string) => {
    setClosed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  /* The flat order the arrow keys walk, in exactly the order drawn. */
  const flat = useMemo(
    () => groups.filter((g) => !isClosed(g.key)).flatMap((g) => g.rows),
    [groups, isClosed],
  );

  /* ---- the batch, derived -------------------------------------------------
     Everything the action bar says is measured off the ROWS THE BOARD IS
     SHOWING, so a facet click that hides a picked row also drops it from the
     count — a verb badge reading `Withdraw 5` over a board holding three of
     them is the same class of lie as a shortcut that resolves to nothing. */
  const batch = useMemo(
    () => rows.filter((c) => picked.has(c.id)),
    [rows, picked],
  );
  /* A settled command cannot be withdrawn: the robot has already run it, and
     offering to undo that would suggest the platform can. So the verb counts
     only what it can actually act on, and the bar says what it is leaving. */
  const batchOpen = useMemo(() => batch.filter((c) => IN_FLIGHT.has(c.status)), [batch]);

  /* A selection that survives a filter it no longer matches is a selection the
     operator cannot see. Prune to the visible set on every read. */
  useEffect(() => {
    setPicked((prev) => {
      if (prev.size === 0) return prev;
      const live = new Set(rows.map((c) => c.id));
      let changed = false;
      const next = new Set<number>();
      for (const id of prev) {
        if (live.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [rows]);

  /* Arming is never carried across a change of batch — if the set moved, the
     count on the button moved, and a press meant for the old count must not
     land on the new one. */
  useEffect(() => {
    setBatchArmed(false);
    setBatchError(null);
  }, [picked]);

  const togglePick = useCallback(
    (c: LedgerCommand, extend: boolean) => {
      setPicked((prev) => {
        const next = new Set(prev);
        const from = anchor.current;
        if (extend && from !== null && from !== c.id) {
          const a = flat.findIndex((x) => x.id === from);
          const b = flat.findIndex((x) => x.id === c.id);
          if (a !== -1 && b !== -1) {
            /* A shift-run ADDS; it never clears what is already picked, which
               is how an operator builds a batch out of two lifecycle groups. */
            for (let i = Math.min(a, b); i <= Math.max(a, b); i += 1) {
              const row = flat[i];
              if (row !== undefined) next.add(row.id);
            }
            anchor.current = c.id;
            return next;
          }
        }
        if (next.has(c.id)) next.delete(c.id);
        else next.add(c.id);
        anchor.current = c.id;
        return next;
      });
    },
    [flat],
  );

  const pickAll = useCallback(() => {
    setPicked((prev) => (prev.size >= rows.length && rows.length > 0
      ? new Set<number>()
      : new Set(rows.map((c) => c.id))));
    anchor.current = null;
  }, [rows]);

  /* The group header's own tick. `PENDING 3` is the exact set an operator
     wants to act on, and reaching it by three clicks is three chances to
     miss one. */
  const pickGroup = useCallback((g: Group) => {
    setPicked((prev) => {
      const next = new Set(prev);
      const all = g.rows.every((c) => next.has(c.id));
      for (const c of g.rows) {
        if (all) next.delete(c.id);
        else next.add(c.id);
      }
      return next;
    });
    anchor.current = null;
  }, []);

  const clearBatch = useCallback(() => {
    setPicked(new Set<number>());
    anchor.current = null;
  }, []);

  /* One PATCH per command — the contract has no batch endpoint, and inventing
     one client-side would hide a partial failure. They go out together and the
     result is reported as what it is: how many landed, and how many did not. */
  const withdrawBatch = useCallback(() => {
    if (batchOpen.length === 0 || batchBusy) return;
    setBatchBusy(true);
    setBatchError(null);
    const targets = [...batchOpen];
    void Promise.allSettled(
      targets.map((c) => batchCancel.mutateAsync({ id: c.id, robotId: c.robot_id })),
    ).then((settled) => {
      const ok = settled.filter((r) => r.status === 'fulfilled').length;
      const bad = settled.length - ok;
      setBatchBusy(false);
      setBatchArmed(false);
      if (ok > 0) {
        notifyOk(
          `${ok} command${ok === 1 ? '' : 's'} withdrawn`,
          targets.slice(0, 4).map((c) => `#${c.id}`).join(' ') +
            (targets.length > 4 ? ` +${targets.length - 4}` : ''),
        );
      }
      if (bad > 0) {
        const first = settled.find((r) => r.status === 'rejected');
        const why = first !== undefined && first.status === 'rejected' ? String(first.reason) : '';
        setBatchError(`${bad} of ${settled.length} refused${why === '' ? '' : ` — ${why}`}`);
        notifyError(`${bad} of ${settled.length} could not be withdrawn`, why);
      } else {
        setPicked(new Set<number>());
      }
    });
  }, [batchOpen, batchBusy, batchCancel]);

  /* The one weight that is measured off the rows rather than declared — see
     `textGrow`. Measured over `flat`, the rows the board is actually DRAWING:
     a collapsed `completed 53` is 53 result payloads nobody can see, and
     sizing a column for them is sizing it for a group that is not on screen.
     Everything else in `BOARD_COLS` is fixed. */
  const boardDefs = useMemo<Record<ColKey, ColDef>>(() => {
    const inked = flat.reduce((n, c) => {
      const d = detailOf(c);
      return d.text === '' && d.mark === null ? n : n + 1;
    }, 0);
    return { ...BOARD_COLS, detail: { min: BOARD_COLS.detail.min, grow: textGrow(inked, flat.length) } };
  }, [flat]);
  const boardTemplate = useMemo(() => gridTemplate(boardCols, boardDefs), [boardCols, boardDefs]);

  const selected = useMemo(
    () => all.find((c) => c.id === selectedId) ?? null,
    [all, selectedId],
  );

  /* The board is one tab stop, not a hundred. The selected row takes it; with
     nothing selected the first drawn row does, so the board is always
     reachable and the arrow keys take it from there. */
  const tabStop = useMemo(() => {
    if (selectedId !== null && flat.some((c) => c.id === selectedId)) return selectedId;
    return flat[0]?.id ?? null;
  }, [flat, selectedId]);

  useEffect(() => {
    setConfirmCancel(false);
  }, [selectedId]);

  /* The rail opens on the board's first row — the one the eye lands on — so
     the inspector and the dispatch target are populated without the board
     scrolling somewhere the operator did not ask to go. A starting position,
     not a lock: any other row, or the inspector's own close, replaces it. */
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current) return;
    const head = flat[0];
    if (head === undefined) return;
    opened.current = true;
    setSelectedId(head.id);
    setTarget(head.robot_id);
  }, [flat]);

  const pick = useCallback((c: LedgerCommand) => {
    setSelectedId(c.id);
    setTarget(c.robot_id);
    setRailTab('record');
  }, []);

  /* RE-ISSUE IS A STAGING, NOT A SEND. It points the dispatch rail at the
     command's own robot and hands it the verb and the exact parameters that
     were issued — and stops there. The commit is the same pinned CANCEL/SEND
     pair every other dispatch goes through, with the same arming switch and
     the same pre-flight. Nothing about this path can put a command on a
     machine in one click. */
  const reissue = useCallback((c: LedgerCommand) => {
    setTarget(c.robot_id);
    setRailTab('dispatch');
    setRestage((prev) => ({
      seq: (prev?.seq ?? 0) + 1,
      robotId: c.robot_id,
      verb: c.command,
      params: c.params,
      from: `#${c.id}`,
    }));
  }, []);

  /* A filter that hides the selected row leaves the inspector describing
     something the board is not showing — narrow to `status failed` and the
     rail still reads `#109 pending`, which is a rail that has stopped being
     about this screen. So the selection follows the filter down to the first
     row of the new set.

     It moves the SELECTION only, never the dispatch target: silently
     re-pointing the commit control at another robot because a facet was
     clicked is the exact class of accident this whole panel exists to
     prevent, and CommandPanel stands a staged verb down whenever its target
     changes. Filtering must not be able to do that. */
  useEffect(() => {
    if (selectedId === null) return;
    if (rows.some((c) => c.id === selectedId)) return;
    const head = flat[0];
    if (head === undefined) return;
    setSelectedId(head.id);
  }, [rows, flat, selectedId]);

  /* Keep the row the rail is describing on screen. `nearest` is a no-op when
     it already is, so clicking a visible row never scrolls the board out from
     under the pointer; it only matters when the selection arrived from the
     activity log, the arrow keys, or the opening pick. */
  useEffect(() => {
    if (selectedId === null) return;
    boardRef.current
      ?.querySelector<HTMLElement>(`[data-cmd="${selectedId}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [selectedId, groupBy]);

  /* A verb can be staged from the keyboard while the record is on screen —
     the number row is a global shortcut, by design, so `3` from anywhere puts
     `start_capture` on the bench. It must not stage it out of sight. */
  const onStagedChange = useCallback((verb: string | null) => {
    setStagedVerb(verb);
    if (verb !== null) setRailTab('dispatch');
  }, []);

  const onBoardKey = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (flat.length === 0) return;
      e.preventDefault();
      const i = flat.findIndex((c) => c.id === selectedId);
      const next =
        e.key === 'ArrowDown'
          ? flat[Math.min(flat.length - 1, i + 1)]
          : flat[Math.max(0, i <= 0 ? 0 : i - 1)];
      if (next === undefined) return;
      pick(next);
      boardRef.current
        ?.querySelector<HTMLElement>(`[data-cmd="${next.id}"]`)
        ?.focus({ preventScroll: false });
    },
    [flat, selectedId, pick],
  );

  /* Arrow keys walk the log's DOM focus; the log is one tab stop like the
     board above it. */
  const onFeedKey = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const host = e.currentTarget;
    const rows = [...host.querySelectorAll<HTMLElement>('[data-ev]')];
    const i = rows.indexOf(document.activeElement as HTMLElement);
    if (i === -1) return;
    e.preventDefault();
    const next = rows[e.key === 'ArrowDown' ? Math.min(rows.length - 1, i + 1) : Math.max(0, i - 1)];
    next?.focus();
  }, []);

  /* ---- the activity stream -----------------------------------------------
     A fault that arrives while the operator is looking somewhere else pulses
     its row twice and then holds, which is the only alarm motion THE BAR §7
     permits. It is scoped to `failed` deliberately: at a 3s cadence a pulse
     on every arriving event would be a permanently blinking log, which the
     same rule bans. The baseline is the newest id present on the first load,
     so opening the board never lights up history. */
  const seen = useRef<number | null>(null);
  useEffect(() => {
    if (seen.current === null && ledger.events.length > 0) {
      seen.current = ledger.events.reduce((m, e) => Math.max(m, e.id), 0);
    }
  }, [ledger.events]);

  const feed = useMemo(() => {
    const byId = new Map(all.map((c) => [c.id, c]));
    return ledger.events
      .filter((e) => {
        const c = byId.get(e.command_id);
        return c === undefined ? false : matches(c, null);
      })
      .sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts) || b.id - a.id)
      .slice(0, 200)
      .map((e) => {
        const cmd = byId.get(e.command_id);
        /* THE HOP, NOT THE TOTAL (THE BAR §13 tell 19 — a queue has to show
           time IN state, and a log has to show the time BETWEEN states).
           `Latency` on the board is issue → now; this is the gap between THIS
           transition and the one immediately before it on the same command,
           which is the number that says which leg of the lifecycle is slow.
           The folded row already holds every event in timestamp order, so the
           predecessor is a lookup, not a scan. `issued` has no predecessor and
           renders the em dash rather than a zero. */
        let delta: number | null = null;
        if (cmd !== undefined) {
          const i = cmd.events.findIndex((x) => x.id === e.id);
          const prev = i > 0 ? cmd.events[i - 1] : undefined;
          if (prev !== undefined) {
            const d = Date.parse(e.ts) - Date.parse(prev.ts);
            if (Number.isFinite(d) && d >= 0) delta = d;
          }
        }
        return {
          ev: e,
          /* Only the `issued` event carries the verb; every transition after
             it carries none. The folded command does, so a `sent` line says
             what was sent instead of leaving a column empty on 104 of 211
             rows. Same source, one join. */
          verb: cmd?.command ?? '',
          delta,
          /* THE RAW PAYLOAD THE TRANSITION CARRIED, in the log's last column.
             `result` on a settled transition, the issued parameters on an
             `issued` one, and nothing at all on a `sent` — which is the
             truth: the platform records no body for that hop. */
          payload: payloadOf(e),
        };
      });
  }, [ledger.events, all, matches]);

  /* ---- what "empty" means for each panel ---------------------------------
     `ledger.read` answers whether `/api/audit/commands` returned anything,
     which is the right question for the fault chrome and the WRONG one for
     the first row: with 107 commands read and a filter that matches none of
     them the read is not empty, but the board is — and the empty line never
     fired, so the queue drew its column headings over a void. Each panel
     asks about its own rows, and keeps the ledger's fault state underneath.
     (THE BAR §6: one line of --text-mid where the first row would be.) */
  const feedDefs = useMemo<Record<FeedKey, ColDef>>(() => {
    const inked = feed.reduce(
      (n, f) => (f.payload.text === '' && f.payload.code === null ? n : n + 1),
      0,
    );
    return { ...FEED_COLS, payload: { min: FEED_COLS.payload.min, grow: textGrow(inked, feed.length) } };
  }, [feed]);
  const feedTemplate = useMemo(() => gridTemplate(feedCols, feedDefs), [feedCols, feedDefs]);

  /* Transitions that landed in the last five minutes of the log's own window.
     Measured against the panel's `asOf`, not the wall clock, so a dead read
     cannot let the rate decay to zero as though the fleet had gone quiet. */
  const recentEvents = useMemo(
    () => feed.filter(({ ev }) => feedNow - Date.parse(ev.ts) <= 300_000).length,
    [feed, feedNow],
  );

  const boardRead = useMemo(
    () => ({ ...ledger.read, empty: ledger.read.empty || rows.length === 0 }),
    [ledger.read, rows.length],
  );
  const feedRead = useMemo(
    () => ({ ...ledger.read, empty: ledger.read.empty || feed.length === 0 }),
    [ledger.read, feed.length],
  );

  /* ---- headline readouts -------------------------------------------------- */

  const inFlight = rows.filter((c) => IN_FLIGHT.has(c.status));
  const failed = rows.filter((c) => c.status === 'failed').length;
  const oldest = inFlight.reduce<number | null>((acc, c) => {
    const age = boardNow - Date.parse(c.issued_at);
    return acc === null || age > acc ? age : acc;
  }, null);

  const dispatchTarget = target ?? (robotFilter === '' ? null : robotFilter);

  const selectedRobot = useMemo(
    () => (selected === null ? null : (robots ?? []).find((r) => r.robot_id === selected.robot_id) ?? null),
    [robots, selected],
  );

  /* IS THIS ONE BROKEN, OR IS THIS VERB BROKEN ON THIS MACHINE?

     After reading `FAILURE — PLAN_TIMEOUT` on a `return_home` the operator's
     next question is never about this row: it is whether `return_home` has
     been failing on this rover all week. The ledger already holds the answer,
     so the record prints it — the same verb, the same robot, most recent
     first, with each one's outcome and what it cost. Nothing is derived and
     nothing is aggregated into a rate: five rows of the actual history. */
  const siblings = useMemo(() => {
    if (selected === null) return [];
    return all
      .filter(
        (c) =>
          c.id !== selected.id &&
          c.robot_id === selected.robot_id &&
          c.command === selected.command,
      )
      .sort((a, b) => Date.parse(b.issued_at) - Date.parse(a.issued_at))
      .slice(0, 8);
  }, [all, selected]);

  /* AND WHAT ELSE IS ON THIS MACHINE?

     The question after "has this verb been failing here" is "is anything else
     going on here" — a `resume` sitting pending behind an unacked `stop` is
     the case that matters, and neither row can be read from the other. Same
     ledger, same robot, every OTHER verb, newest first. The two lists are
     kept apart rather than merged because they answer different questions and
     the operator asks them in that order. */
  const stablemates = useMemo(() => {
    if (selected === null) return [];
    return all
      .filter(
        (c) =>
          c.id !== selected.id &&
          c.robot_id === selected.robot_id &&
          c.command !== selected.command,
      )
      .sort((a, b) => Date.parse(b.issued_at) - Date.parse(a.issued_at))
      .slice(0, 8);
  }, [all, selected]);

  /* Only offered where the robot's own published profile still carries the
     verb. A `climb` that a koko has since dropped from its profile is not a
     command this console gets to put back on the bench (THE BAR: the verb set
     is the robot's own, never a hard-coded list). */
  const reissuable = useMemo(() => {
    if (selected === null || selectedRobot === null) return false;
    const profile = readProfile(selectedRobot.command_profile);
    return profile !== null && profile.some((v) => v.name === selected.command);
  }, [selected, selectedRobot]);

  /* The flow strip's x-domain. Computed over the UNFILTERED ledger so the
     ruler holds still while facets are picked: narrowing to one robot must
     thin the marks out, not rescale the axis under them. */
  const windowFrom = useMemo(() => {
    let lo = Number.POSITIVE_INFINITY;
    for (const e of ledger.events) {
      const t = Date.parse(e.ts);
      if (Number.isFinite(t) && t < lo) lo = t;
    }
    for (const c of all) {
      const t = Date.parse(c.issued_at);
      if (Number.isFinite(t) && t < lo) lo = t;
    }
    return Number.isFinite(lo) ? lo : boardNow - 3_600_000;
  }, [ledger.events, all, boardNow]);

  /* ---- render ------------------------------------------------------------- */

  /* The rail's header. Built once and handed to both panes; only one of them
     is on screen, so only one of them draws it. */
  const railTabs = (
    <RailTabs
      active={railTab}
      onChange={setRailTab}
      recordId={selected?.id ?? null}
      recordTone={selected === null ? null : CMD_TONE[selected.status]}
      staged={stagedVerb}
      target={dispatchTarget}
    />
  );

  /* THE SURFACE'S OWN LIVE STATE, ON THE IDENTITY ROW.

     These four are what the board IS — how much is outstanding, how long the
     oldest of it has waited, how much broke, and what a dispatch costs — and
     `SurfaceShell`'s subtitle slot is exactly "live state belonging to the
     object". They used to sit at the far end of the filter toolbar, which
     left a 20px title alone on a 1450px row and filed the surface's headline
     numbers under its controls.

     They are measured over the ROWS THE BOARD IS SHOWING, so a facet click
     restates them — and says so, because `0 open` under a `status failed`
     filter is a true statement about a filtered board and a false one about
     the fleet. */
  const headline = (
    <div className={styles.readouts}>
      {activeFilters.length > 0 && (
        <span className={styles.readoutScope} title={`Measured over the filtered board: ${activeFilters.join(' · ')}`}>
          filtered
        </span>
      )}
      {/* `0 OPEN … 0 FAILED` beside `— OLDEST … — P50 LATENCY` was one strip
          using two conventions for one fact: the two derived figures already
          knew to withhold themselves and the two counts did not. */}
      <Readout
        label="open"
        value={String(figure(boardSources, inFlight.length))}
        title="Commands that have not settled — pending, sent or acked"
      />
      <Readout
        label="oldest"
        value={oldest === null ? NO_VALUE : duration(oldest)}
        /* Banded off the same ladder the rows are, so the headline and
           the row it is pointing at can never disagree about whether
           the queue's oldest wait is merely slow or stuck. */
        tone={
          oldest === null ? undefined
            : oldest >= ladder.stuck ? 'error'
              : oldest >= ladder.p95 ? 'warn'
                : undefined
        }
        title="Longest a command has been waiting since it was issued"
      />
      <Readout
        label="failed"
        value={String(figure(boardSources, failed))}
        tone={!boardBlind && failed > 0 ? 'error' : undefined}
        title="Commands in the window that ended in failure"
      />
      <Readout
        label="p50 latency"
        value={latencyText(latency.p50)}
        title={`Median issue → settle over the ${latency.n} commands in this window that finished. The same measurement the flow plot rules on and the queue's duration columns ink against.`}
      />
    </div>
  );

  return (
    <SurfaceShell
      eyebrow="Command & control"
      title="Command queue"
      subtitle={headline}
      pad="none"
    >
      <div className={styles.page}>
        <Toolbar label="Queue filters">
          <SearchInput
            aria-label="Filter the queue"
            placeholder="id · robot · verb · issuer"
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
          />
          <span className={styles.pick}>
            <Select
              size="sm"
              mono
              aria-label="Filter by robot"
              value={robotFilter}
              onChange={(e) => setRobotFilter(e.currentTarget.value)}
            >
              <option value="">all robots</option>
              {(robots ?? []).map((r) => (
                <option key={r.robot_id} value={r.robot_id}>{r.robot_id}</option>
              ))}
            </Select>
          </span>
          <ToolbarDivider />
          <ToolbarLabel>Group by</ToolbarLabel>
          <SegmentedControl
            items={GROUP_ITEMS}
            value={groupBy}
            onChange={setGroupBy}
            label="Group the queue by"
          />
          <ToolbarSpacer />
          {/* What the board is currently narrowed to, at the toolbar's far
              end, with one dismiss that clears the lot. It sits opposite the
              controls that set it rather than between them. */}
          {activeFilters.length > 0 && (
            <Chip mono onDismiss={clearFilters} dismissLabel="Clear every filter">
              {activeFilters.join(' · ')}
            </Chip>
          )}
        </Toolbar>

        <div className={styles.grid}>
          {/* ---- FACETS ---------------------------------------------------- */}
          <Panel
            className={styles.facets}
            title="Filters"
            count={figure(
              boardSources,
              rows.length === all.length ? all.length : `${rows.length}/${all.length}`,
            )}
            /* The rail is 232px and the panel title has to survive inside
               it: `Filters (109)` plus `Live off 22s` is already the whole
               header, so the audit window's size lives on the Activity panel
               that actually reads it, not here as well. */
            meta={<FeedTag health={boardH} now={now} pulse={connected} read={boardSources} />}
            pad="none"
            scroll={false}
          >
            <div className={styles.facetFrame}>
              {/* The three pickable facets scroll; the latency profile does
                  not. It is not a filter, it is the shape of the set the
                  three above have selected, and a summary that scrolls off at
                  a laptop height is a summary nobody ever reads — at 1280 the
                  rail never reached it at all. Pinned, it is the rail's
                  floor, the way the commit pair is the dispatch pane's. */}
              <div
                className={styles.facetScroll}
                ref={facetRef}
                data-edge={facetEdge}
                data-stale={!boardH.live}
              >
                <Section
                  label="Status"
                  aside={<span>{statusFilter === null ? 'all' : statusFilter}</span>}
                >
                  <Distribution
                    items={statusFacets}
                    selected={statusFilter}
                    onSelect={(k) => setStatusFilter((p) => (p === k ? null : k))}
                    barWidth={FACET_BAR}
                  />
                </Section>
                <FacetSection
                  label="Verb"
                  items={verbFacets}
                  read={boardSources}
                  cap={VERB_CAP}
                  selected={verbFilter}
                  onSelect={(k) => setVerbFilter((p) => (p === k ? null : k))}
                  expanded={openFacets.has('command')}
                  onToggle={() => toggleFacet('command')}
                />
                <FacetSection
                  label="Issuer"
                  items={issuerFacets}
                  read={boardSources}
                  cap={ISSUER_CAP}
                  selected={issuerFilter}
                  onSelect={(k) => setIssuerFilter((p) => (p === k ? null : k))}
                  expanded={openFacets.has('issuer')}
                  onToggle={() => toggleFacet('issuer')}
                />
              </div>
              <div className={styles.facetFoot} data-stale={!boardH.live}>
                <div className={styles.facetFootHead}>
                  <Eyebrow>Issue → settle</Eyebrow>
                  {/* THE LADDER, PUBLISHED. These are not two decorative
                      readouts: they are the edges the board's `Latency` and
                      `In state` columns recolour at, printed in the exact
                      inks those columns will use, so the key IS the
                      measurement (THE BAR §11.4 — thresholds are published in
                      the UI, not hidden in code). Beneath them the histogram's
                      last bar carries the third edge, `> 5m`, in --error.  */}
                  <span
                    className={styles.ladderKey}
                    title={
                      ladder.measured
                        ? `The board's duration columns ink against this window: under ${duration(ladder.p50)} reads plain, ${duration(ladder.p50)}–${duration(ladder.p95)} bright, past ${duration(ladder.p95)} amber, and past ${duration(ladder.tail)} settled (or ${duration(ladder.stuck)} still waiting) red. Measured over ${ladder.n} settled commands.`
                        : `Only ${ladder.n} settled command${ladder.n === 1 ? '' : 's'} in this window — too few to measure a p95, so the duration columns ink against fixed edges of ${duration(ladder.p50)} and ${duration(ladder.p95)}.`
                    }
                  >
                    <span className={styles.ladderP50}>{duration(ladder.p50)}</span>
                    <span className={styles.ladderSep}>·</span>
                    <span className={styles.ladderP95}>{duration(ladder.p95)}</span>
                    {!ladder.measured && <span className={styles.ladderFixed}>fixed</span>}
                  </span>
                </div>
                {latency.n === 0 ? (
                  /* One phrasing per fact, from `blankLine`. */
                  <Empty of={boardSources}>
                    {blankLine('nothing', { noun: 'settled commands', where: 'in this window' })}
                  </Empty>
                ) : (
                  <Distribution items={latency.items} barWidth={FACET_BAR} />
                )}
              </div>
            </div>
          </Panel>

          {/* ---- THE BOARD --------------------------------------------------
              SELECTION SWAPS THE HEADER FOR AN ACTION BAR (THE BAR §5). Same
              28px row, same position, so nothing under it shifts when rows are
              picked; the transport word and the freshness tag stand down for as
              long as the operator is holding a batch, because while a batch is
              held the header's job is to say what is in it and what will happen
              to it. */}
          <Panel
            className={styles.board}
            title={
              batch.length > 0
                ? <span className={styles.batchCount}>{batch.length} selected</span>
                : 'Queue'
            }
            count={batch.length > 0 ? undefined : figure(boardSources, rows.length)}
            meta={
              batch.length > 0 ? (
                <div className={styles.batchBar}>
                  {/* What the verb will and will not touch, before it is
                      pressed. A batch of five that holds two settled commands
                      is a batch the platform can only act on three of. */}
                  <span className={styles.batchScope}>
                    {batchOpen.length} in flight
                    {batch.length > batchOpen.length && (
                      <>
                        <span className={styles.batchSep}>·</span>
                        {batch.length - batchOpen.length} settled, kept
                      </>
                    )}
                  </span>
                  {batchError !== null && (
                    <span className={styles.batchError}>{batchError}</span>
                  )}
                  {batchArmed ? (
                    <>
                      <Button size="sm" variant="secondary" onClick={() => setBatchArmed(false)}>
                        Keep
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        icon="x-circle"
                        badge={batchOpen.length}
                        disabled={batchBusy}
                        title={`Withdraw ${batchOpen.map((c) => `#${c.id}`).join(' ')}`}
                        onClick={withdrawBatch}
                      >
                        {batchBusy ? 'Withdrawing' : 'Confirm withdraw'}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="danger"
                      icon="x-circle"
                      badge={batchOpen.length}
                      disabled={batchOpen.length === 0}
                      title={
                        batchOpen.length === 0
                          ? 'Every command in this batch has already settled'
                          : `Withdraw ${batchOpen.length} command${batchOpen.length === 1 ? '' : 's'} — press again to send`
                      }
                      onClick={() => setBatchArmed(true)}
                    >
                      Withdraw
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={clearBatch}>Clear</Button>
                </div>
              ) : (
                <>
                  <span
                    className={styles.window}
                    data-tone={boardH.live ? undefined : 'warn'}
                    title={
                      connected
                        ? 'Transitions arrive over MQTT as they happen'
                        : 'MQTT is unavailable here — the board re-reads every 3s instead'
                    }
                  >
                    {connected ? 'push' : 'poll 3s'}
                  </span>
                  <FeedTag health={boardH} now={now} pulse={connected} read={boardSources} />
                </>
              )
            }
            loading={ledger.loading}
            pad="none"
          >
            {/* A read that fails is a transport fault, not a robot fault: it
                is --warn, it names the endpoint and the last good read, and
                it says in words what the dimmed rows underneath now are. The
                browser's own `TypeError: Failed to fetch` is kept, at the end,
                in --text-low, where it belongs.

                ONLY WHERE THERE ARE ROWS TO DATE. This line's whole job is to
                say "everything below is that read, not now"; with nothing
                below it says nothing, and the `ReadBody` under the column
                headings is already drawing the endpoint, the class and the
                runs lost at the first row's position. Both drew during a total
                outage — the same sentence twice, 50px apart, once above the
                headings and once below them. */}
            {!boardH.live && !boardBlind && (
              <StaleLine health={boardH} now={now} read={boardSources} />
            )}
            <div
              className={styles.boardScroll}
              /* A GRID, DECLARED. The rows stopped being <button>s when they
                 grew a selection tick — a button cannot legally hold one — so
                 the structure a screen reader was getting from the button is
                 now stated: a multi-selectable grid of rows of cells, which is
                 also the only shape in which `aria-selected` on a row means
                 anything. */
              role="grid"
              aria-label="Command queue"
              aria-multiselectable="true"
              aria-rowcount={rows.length}
              ref={(n) => {
                boardRef.current = n;
                boardWidthRef(n);
              }}
              onKeyDown={onBoardKey}
              data-group={groupBy}
              data-stale={!boardH.live}
            >
              <div
                className={styles.colhead}
                role="row"
                style={{ gridTemplateColumns: boardTemplate }}
              >
                {boardCols.map((k) => (
                  <BoardHead
                    key={k}
                    col={k}
                    sort={sort}
                    onSort={onSort}
                    ladder={ladder}
                    allPicked={rows.length > 0 && batch.length === rows.length}
                    somePicked={batch.length > 0}
                    onPickAll={pickAll}
                    pickCount={rows.length}
                  />
                ))}
              </div>

              {/* The board's own row grid IS the instrument — its ten columns
                  and its row pitch are true before the first read, when the
                  read fails, and when it lands with nothing in it — so
                  `ReadBody` drives the state and the queue keeps its shape in
                  all five. `Reading the queue…` used to sit here for as long
                  as a dead API kept refusing. */}
              <ReadBody
                read={boardRead}
                skeleton={<GridNote />}
                empty={
                  activeFilters.length > 0
                    ? (
                      <Blank
                        kind="filtered"
                        noun="commands"
                        detail={activeFilters.join(' · ')}
                        action={{ label: 'Clear', onSelect: clearFilters }}
                      />
                    )
                    : <Blank noun="commands" where="in this window" />
                }
              >
              {groups.map((g) => {
                  const shut = isClosed(g.key);
                  /* TWO DIFFERENT QUESTIONS, BOTH NAMED (THE BAR §13 tell 24).
                     While a group still holds unsettled work the header's age
                     is how long the oldest of it has been WAITING, and it
                     carries the same band ink the row would — so a collapsed
                     `acked 10` still shows that it is rotting. Once every row
                     in the group has settled there is nothing waiting, and the
                     number becomes how long ago the group last moved, which
                     is history and renders dim. */
                  let oldestWait: number | null = null;
                  let oldestAny = 0;
                  const waitBands: (Band | null)[] = [];
                  for (const c of g.rows) {
                    const age = boardNow - Date.parse(c.updated_at);
                    if (age > oldestAny) oldestAny = age;
                    if (IN_FLIGHT.has(c.status)) {
                      if (oldestWait === null || age > oldestWait) oldestWait = age;
                      waitBands.push(waitBand(age, c.status, ladder));
                    }
                  }
                  const waiting = oldestWait !== null;
                  const headBand = waiting ? worstBand(waitBands) : null;
                  const inBatch = g.rows.filter((c) => picked.has(c.id)).length;
                  return (
                    <div key={g.key} className={styles.group} role="rowgroup">
                     <div
                       className={styles.groupBar}
                       role="row"
                       data-tone={g.tone ?? 'neutral'}
                       data-picked={inBatch > 0}
                     >
                      {/* The group's own tick, in the same gutter the rows'
                          ticks stand in, so a column of checkboxes runs
                          straight down the board's leading edge. */}
                      <span role="gridcell" className={styles.groupPick}>
                        <CheckBox
                          checked={inBatch === g.rows.length && g.rows.length > 0}
                          indeterminate={inBatch > 0 && inBatch < g.rows.length}
                          onChange={() => pickGroup(g)}
                          aria-label={`Select the ${g.rows.length} commands in this group`}
                          title={`Select all ${g.rows.length} in this group`}
                        />
                      </span>
                      <span role="gridcell" className={styles.groupHeadCell}>
                      <button
                        type="button"
                        className={styles.groupHead}
                        aria-expanded={!shut}
                        title={g.hint}
                        onClick={() => toggleGroup(g.key)}
                      >
                        <Icon name={shut ? 'chevron-right' : 'chevron-down'} size={12} />
                        {g.label}
                        <span className={styles.groupCount}>{g.rows.length}</span>
                        {/* THE TRACK, DRAWN ONCE PER GROUP RATHER THAN ONCE PER
                            ROW. Grouped by state every row's track is identical
                            and identical to this one, so the column comes off
                            the rows (see GROUP_COL) and the glyph stays here,
                            where it is the header's own statement of how far
                            this state means a command got. */}
                        {g.status !== null && <LifecycleTrack status={g.status} />}
                        <span className={styles.groupSpacer} />
                        {/* Grouped by robot, verb or issuer, the one thing the
                            header can say that the rows cannot is how much of
                            this group is broken. */}
                        {g.fails > 0 && (
                          <span className={styles.groupFails} title={`${g.fails} failed`}>
                            {g.fails} failed
                          </span>
                        )}
                        <span className={styles.groupAge}>
                          <span
                            className={styles.groupAgeWord}
                            title={
                              waiting
                                ? 'How long the oldest command in this group that has not settled has been waiting'
                                : 'Every command in this group has settled — how long ago the most recent one did'
                            }
                          >
                            {waiting ? 'waiting' : 'settled'}
                          </span>
                          <BandedAge
                            ms={waiting ? oldestWait : oldestAny}
                            band={headBand}
                            note={bandNote(headBand, ladder, 'wait')}
                          />
                        </span>
                      </button>
                      </span>
                     </div>
                      {!shut &&
                        g.rows.map((c) => (
                          <Row
                            key={c.id}
                            cmd={c}
                            robot={robotIndex.get(c.robot_id) ?? null}
                            cols={boardCols}
                            template={boardTemplate}
                            now={boardNow}
                            ladder={ladder}
                            selected={c.id === selectedId}
                            picked={picked.has(c.id)}
                            tabbable={c.id === (tabStop ?? -1)}
                            onPick={pick}
                            onPickToggle={togglePick}
                          />
                        ))}
                    </div>
                  );
              })}
              </ReadBody>
            </div>
          </Panel>

          {/* ---- ACTIVITY ---------------------------------------------------- */}
          <Panel
            className={styles.activity}
            title="Activity"
            /* The activity stream reads ONE endpoint, so it answers on that
               one alone rather than on the board's pair. */
            count={figure(ledger.read, feed.length)}
            meta={
              <>
                {/* HOW FAST THE LOG IS MOVING, not just how long it is. A
                    stream that has stopped and a stream that is quiet look
                    identical in a table of timestamps; this is the number that
                    tells them apart, and it is counted over the same window
                    the rows below are drawn from. */}
                <span
                  className={styles.window}
                  title={`${recentEvents} lifecycle transition${recentEvents === 1 ? '' : 's'} in the five minutes to ${utcTime(new Date(feedNow).toISOString())} — the log's own arrival rate over the rows below.`}
                >
                  <Figure of={ledger.read} value={recentEvents} /> in 5m
                </span>
                <span
                  className={styles.window}
                  data-tone={auditH.live ? undefined : 'warn'}
                  title={
                    ledger.clipped
                      ? `/api/audit/commands returned its ${AUDIT_WINDOW}-event cap — there is older history this board is not showing`
                      : `/api/audit/commands returned every event it holds (cap ${AUDIT_WINDOW})`
                  }
                >
                  audit <Figure of={ledger.read} value={ledger.eventCount} />
                  {ledger.clipped ? ' capped' : ''}
                </span>
                <FeedTag health={auditH} now={now} pulse={connected} read={ledger.read} />
              </>
            }
            pad="none"
            scroll={false}
          >
            <div
              className={styles.feedFrame}
              data-stale={!auditH.live}
              ref={feedWidthRef}
            >
            <div
              className={styles.feedHead}
              role="row"
              style={{ gridTemplateColumns: feedTemplate }}
            >
              {feedCols.map((k) => <FeedHead key={k} col={k} />)}
            </div>
            <div className={styles.feed} onKeyDown={onFeedKey}>
              <ReadBody
                read={feedRead}
                skeleton={<GridNote />}
                empty={
                  activeFilters.length > 0
                    ? (
                      <Blank
                        kind="filtered"
                        noun="lifecycle events"
                        detail={activeFilters.join(' · ')}
                        action={{ label: 'Clear', onSelect: clearFilters }}
                      />
                    )
                    : <Blank noun="lifecycle events" where="in this window" />
                }
              >
              {feed.map(({ ev, verb, delta, payload }, i) => (
                  <FeedRow
                    key={ev.id}
                    ev={ev}
                    verb={verb}
                    delta={delta}
                    payload={payload}
                    build={buildOf(robotIndex.get(ev.robot_id) ?? null)}
                    cols={feedCols}
                    template={feedTemplate}
                    now={feedNow}
                    selected={ev.command_id === selectedId}
                    fresh={ev.status === 'failed' && seen.current !== null && ev.id > seen.current}
                    tabbable={i === 0}
                    /* A line in the log opens the record it is about — the
                       same act as clicking the board row, so it lands the
                       rail in the same place. */
                    onPick={() => {
                      setSelectedId(ev.command_id);
                      setTarget(ev.robot_id);
                      setRailTab('record');
                    }}
                  />
              ))}
              </ReadBody>
            </div>
            </div>
          </Panel>

          {/* ---- THE RAIL ----------------------------------------------------
              One pane at a time, and the panel header is the switch. See the
              note on `railTab` above for why these two do not share a rail. */}
          <div className={styles.rail}>
            <Inspector
              tabs={railTab === 'record' ? railTabs : 'Record'}
              off={railTab !== 'record'}
              siblings={siblings}
              stablemates={stablemates}
              onPickSibling={pick}
              cmd={selected}
              robot={selectedRobot}
              reissuable={reissuable}
              onReissue={reissue}
              now={boardNow}
              ladder={ladder}
              health={boardH}
              busy={cancel.isPending}
              error={cancel.isError ? String(cancel.error) : null}
              confirming={confirmCancel}
              onConfirm={() => setConfirmCancel(true)}
              onAbandon={() => setConfirmCancel(false)}
              onCancel={(c) => {
                cancel.mutate(
                  { id: c.id, robotId: c.robot_id },
                  {
                    onSuccess: () => {
                      notifyOk(`#${c.id} cancelled`, `${c.command} · ${c.robot_id}`);
                      setConfirmCancel(false);
                    },
                  },
                );
              }}
              onClose={() => setSelectedId(null)}
            />
            <CommandPanel
              ladder={ladder}
              title={railTab === 'dispatch' ? railTabs : 'Dispatch'}
              className={
                railTab === 'dispatch' ? styles.dispatch : `${styles.dispatch} ${styles.paneOff}`
              }
              robotId={dispatchTarget}
              stageRequest={restage}
              /* Selected, so the board highlights what just left — but the
                 rail STAYS on the bench. The command is not in the ledger for
                 another poll, and switching to a record pane that has to say
                 "none selected" for a second is the wrong answer to "did that
                 send". The bench's own IN FLIGHT list picks it up instead. */
              onIssued={setSelectedId}
              onStagedChange={onStagedChange}
              targetControl={
                <span className={styles.pick}>
                  <Select
                    size="sm"
                    mono
                    aria-label="Robot to dispatch to"
                    value={dispatchTarget ?? ''}
                    onChange={(e) =>
                      setTarget(e.currentTarget.value === '' ? null : e.currentTarget.value)
                    }
                  >
                    <option value="">no target</option>
                    {(robots ?? []).map((r) => (
                      <option key={r.robot_id} value={r.robot_id}>{r.robot_id}</option>
                    ))}
                  </Select>
                </span>
              }
            />
          </div>
        </div>

        {/* ---- THE TIME AXIS ------------------------------------------------
            One ruler under the whole grid, the way every reference pins its
            transport bar. Bound to the board's own filter, so picking `failed`
            leaves only the failures standing on it. */}
        <FlowStrip
          className={styles.flow}
          commands={rows}
          from={windowFrom}
          now={boardNow}
          clock={now}
          read={boardSources}
          health={boardH}
          selectedId={selectedId}
          onPick={pick}
          connected={connected}
          filtered={activeFilters.length > 0}
          capped={ledger.clipped}
        />
      </div>
    </SurfaceShell>
  );
}

/* --------------------------------------------------------------------------
   A sortable column header. The arrow is inline after the label
   (THE BAR §5) and the cell keeps the header's own type and colour, so an
   unsorted board still reads as a table of labels rather than a row of
   buttons — the affordance is the hover, the state is the arrow.
   -------------------------------------------------------------------------- */

function SortHead({
  k,
  sort,
  onSort,
  children,
  title,
  right,
  className,
}: {
  k: SortKey;
  sort: Sort | null;
  onSort: (k: SortKey) => void;
  children: ReactNode;
  title: string;
  right?: boolean;
  className?: string | undefined;
}) {
  const on = sort !== null && sort.key === k;
  const cls = [styles.sortHead, right === true ? styles.hRight : '', className ?? '']
    .filter((x) => x !== '')
    .join(' ');
  return (
    <button
      type="button"
      className={cls}
      data-on={on}
      /* `aria-sort` belongs on the header CELL in a real grid; this board is a
         CSS grid of buttons, so the state goes where a reader will find it —
         on the control, in words. */
      aria-label={`${title}. ${
        on ? `Sorted ${sort.dir === 'asc' ? 'ascending' : 'descending'}` : 'Not sorted'
      }`}
      title={`${title}\nClick to sort`}
      onClick={() => onSort(k)}
    >
      {/* The arrow takes the side AWAY from the column's own alignment, so the
          label's edge stays exactly where the values under it are: a right
          column's header sits flush right with the arrow inboard of it, a left
          column's the mirror. Its slot is reserved either way, so sorting
          never moves the label a pixel (§7). */}
      {right === true && (
        <span className={styles.sortArrow} aria-hidden="true">
          {on ? (sort.dir === 'asc' ? '↑' : '↓') : ''}
        </span>
      )}
      {children}
      {right !== true && (
        <span className={styles.sortArrow} aria-hidden="true">
          {on ? (sort.dir === 'asc' ? '↑' : '↓') : ''}
        </span>
      )}
    </button>
  );
}

/* --------------------------------------------------------------------------
   One queue row. 26px, one line, a 2px state edge at the leading edge and the
   time-in-state badge right-aligned in mono.
   -------------------------------------------------------------------------- */

/* ==========================================================================
   ONE LOADING DESIGN ON THIS CONSOLE, AND IT IS NOT THIS ONE.

   This used to draw 180 ragged grey pills — ten rows of ten cells over the
   Queue and eight rows of seven over the Activity feed — on the theory that a
   block per cell promises the shape of the row that is coming. Two things were
   wrong with it, and the second is the one that matters:

     · IT WAS A SECOND VOCABULARY. /fleet, /fleet/detail, /telemetry and
       /catalog all answer a first read the same way — the panel keeps its
       column headings, a 1px indeterminate bar runs under the header, and one
       line of --text-mid reads `Reading…` with the wait on it. An operator who
       has learnt that idiom on five surfaces should not have to learn a sixth
       here. THE BAR §6 is explicit: "the panel keeps its header and shows a
       1px indeterminate bar under it… never a skeleton shimmer".
     · A GRID OF BLOCKS IS A PROMISE. It says rows are on their way, in the
       state where the console does not know that: 180 bright blocks over a
       dead endpoint is the loudest possible way to be wrong.

   The column headings above this are the instrument and they are drawn by the
   panel itself in every state, so nothing is lost by deleting the blocks. What
   is left is the one line, at the first row's position, and `useStanding`
   already knows which of the three sentences it is — `Reading… 7s`, `Nothing
   has been read.`, or the caller's absence line.
   ========================================================================== */

function GridNote() {
  const { note } = useStanding();
  return <>{note}</>;
}

/* --------------------------------------------------------------------------
   One column header. Driven by the same array the rows are, so a column can
   never appear in one and not the other.
   -------------------------------------------------------------------------- */

function BoardHead({
  col,
  sort,
  onSort,
  ladder,
  allPicked,
  somePicked,
  onPickAll,
  pickCount,
}: {
  col: ColKey;
  sort: Sort | null;
  onSort: (k: SortKey) => void;
  ladder: Ladder;
  /** Every row the board is currently showing is selected. */
  allPicked: boolean;
  /** Some, but not all, are — the checkbox's third state. */
  somePicked: boolean;
  onPickAll: () => void;
  /** How many rows the board is showing, for the control's own name. */
  pickCount: number;
}) {
  switch (col) {
    case 'edge':
      return <span />;
    case 'pick':
      return (
        <span className={styles.pickCell}>
          {/* Dead over an empty board. A read that has not landed leaves no
              rows to select, and an enabled control over nothing is the same
              claim as a figure printed over nothing. */}
          <CheckBox
            checked={allPicked}
            indeterminate={somePicked && !allPicked}
            disabled={pickCount === 0}
            onChange={onPickAll}
            aria-label={
              allPicked
                ? 'Clear the selection'
                : `Select all ${pickCount} commands the board is showing`
            }
            title={
              pickCount === 0
                ? 'Nothing on the board to select'
                : allPicked
                  ? 'Clear the selection'
                  : `Select all ${pickCount} commands the board is showing`
            }
          />
        </span>
      );
    case 'id':
      return <SortHead sort={sort} onSort={onSort} k="id" right title="Command id">#</SortHead>;
    case 'robot':
      return (
        <SortHead sort={sort} onSort={onSort} k="robot" title="The robot the command was addressed to">
          Robot
        </SortHead>
      );
    case 'site':
      return (
        <SortHead
          sort={sort}
          onSort={onSort}
          k="site"
          title="Where the target is standing, from its registry row. A run of stuck commands at one site is a site problem, not a robot problem."
        >
          Site
        </SortHead>
      );
    case 'build':
      return (
        <SortHead
          sort={sort}
          onSort={onSort}
          k="build"
          title="The build the target is running NOW — its `firmware`, which its command profile republishes as `platform_version`. Not the build it was running when the command was issued: the registry carries one version per robot and no history. Sorted numerically per segment, so 2.9.4 comes before 2.10.7."
        >
          Build
        </SortHead>
      );
    case 'link':
      return (
        <SortHead
          sort={sort}
          onSort={onSort}
          k="link"
          title="Whether the target is reachable NOW, from /api/robots — not at the time of issue. A pending command addressed to an offline robot is a command that is not going anywhere. Sorted worst first."
        >
          Link now
        </SortHead>
      );
    case 'verb':
      return (
        <SortHead sort={sort} onSort={onSort} k="command" title="The verb, from the robot's own command profile">
          Verb
        </SortHead>
      );
    case 'stage':
      return (
        <span title="issue → sent → ack → settle. A hatched slot is one the platform recorded no transition through.">
          Stage
        </span>
      );
    case 'detail':
      return (
        <span
          className={styles.headDetail}
          title="What this command carries: the failure that ended it, who withdrew it, what it produced, or the parameters it was issued with. Last, and left-aligned, because it is the one column that can hold a sentence."
        >
          Detail
        </span>
      );
    case 'issuer':
      return <span title="Who issued it — an operator address, or one of the platform's own dispatchers">By</span>;
    case 'issued':
      return (
        <SortHead sort={sort} onSort={onSort} k="issued" right title="When the command was issued, UTC">
          Issued
        </SortHead>
      );
    case 'latency':
      return (
        <SortHead
          sort={sort}
          onSort={onSort}
          k="latency"
          right
          title={`Issue to the state the command is in now. Inked against this window's issue → settle distribution: plain under the median (${duration(ladder.p50)}), bright to p95 (${duration(ladder.p95)}), amber past it, red past ${duration(ladder.tail)}. Pending commands have no measurement and sort last.`}
        >
          Latency
        </SortHead>
      );
    case 'instate':
      return (
        <SortHead
          sort={sort}
          onSort={onSort}
          k="instate"
          right
          title={`Time since the command entered its current state. On the three in-flight statuses it inks against the same ladder — amber past p95 (${duration(ladder.p95)}), red past ${duration(ladder.stuck)}, at which point the command is stuck rather than slow. On a settled command it is how long ago it finished, and stays dim.`}
        >
          In state
        </SortHead>
      );
  }
}

/* --------------------------------------------------------------------------
   One queue row. 26px, one line, a 2px state edge at the leading edge and the
   time-in-state badge right-aligned in mono. The cells it draws are exactly
   the ones the header drew, in the same order.
   -------------------------------------------------------------------------- */

function Row({
  cmd,
  robot,
  cols,
  template,
  now,
  ladder,
  selected,
  picked,
  tabbable,
  onPick,
  onPickToggle,
}: {
  cmd: LedgerCommand;
  /** The target's registry row, for the three TARGET columns. Null while
      /api/robots is reading, or for a robot the registry does not hold — in
      both cases the cells render the em dash rather than a guess. */
  robot: Robot | null;
  cols: readonly ColKey[];
  template: string;
  now: number;
  /** The window's own p50/p95 and the two ceilings, from the facet rail. */
  ladder: Ladder;
  selected: boolean;
  /** In the batch the action bar is about to act on. */
  picked: boolean;
  /** Roving tab index: one stop for the whole board, arrows walk it. */
  tabbable: boolean;
  onPick: (c: LedgerCommand) => void;
  /** Shift extends the batch from the last row touched, as every list does. */
  onPickToggle: (c: LedgerCommand, extend: boolean) => void;
}) {
  const tone = CMD_TONE[cmd.status];
  const detail = detailOf(cmd);
  const lat = latencyMs(cmd);
  const inState = now - Date.parse(cmd.updated_at);
  /* The board's two duration columns, inked against the window's own
     measurement rather than printed flat. */
  const latBand = latencyBand(lat, ladder);
  const waitB = waitBand(inState, cmd.status, ladder);
  const build = buildOf(robot);
  const site = siteOf(robot);

  const cell = (k: ColKey) => {
    switch (k) {
      case 'edge':
        return <span key={k} role="gridcell" className={styles.edge} aria-hidden="true" />;
      case 'pick':
        return (
          <span key={k} role="gridcell" className={styles.pickCell}>
            {/* NOT A TAB STOP, BY THE CONSOLE'S OWN RULE. A row is the
                composite and its tick is a cell in it, so the tick is reached
                from the row rather than from the tab sequence —
                `components/motion/widgets.ts` enforces exactly this across
                every selectable list in the build and publishes `x` on the box
                as `aria-keyshortcuts`, which is the key the sheet already
                prints. Declared here as well so the first paint matches. */}
            <CheckBox
              checked={picked}
              tabIndex={-1}
              aria-label={`Select #${cmd.id} ${cmd.command} on ${cmd.robot_id}`}
              title={`Select #${cmd.id} — x on the focused row. Shift-click takes the whole run from the last one.`}
              onChange={() => undefined}
              /* `click`, not `change`: the modifier state is on the mouse
                 event and a change handler never sees it, which is what makes
                 shift-range selection possible from the keyboard too — Space
                 fires a click with `shiftKey` false. */
              onClick={(e) => {
                e.stopPropagation();
                onPickToggle(cmd, e.shiftKey);
              }}
            />
          </span>
        );
      case 'id':
        return <span key={k} role="gridcell" className={styles.id}>{cmd.id}</span>;
      case 'robot':
        return <span key={k} role="gridcell" className={styles.robot}>{cmd.robot_id}</span>;
      case 'site':
        return (
          <span key={k} role="gridcell" className={styles.site} title={site ?? 'no site on the registry row'}>
            {site ?? NO_VALUE}
          </span>
        );
      case 'build':
        return (
          <span
            key={k}
            role="gridcell"
            className={styles.build}
            data-known={build !== null}
            title={
              build === null
                ? `No build read for ${cmd.robot_id}`
                : `${cmd.robot_id} is running ${build} now`
            }
          >
            {build ?? NO_VALUE}
          </span>
        );
      /* THE CURATED WORD, not the wire enum. `mono` is for state that came off
         the wire as an enum (see StatusBadge.module.css) — a command's `acked`
         is one, a robot's link is not: it has a curated label the other five
         surfaces all print. This rail and the Link row below it were the only
         places in the console that said `live` and `stale` in lowercase mono
         while the register, the catalog, telemetry, the robot page and teleop
         all said `Live` and `Stale`. One fact, one spelling. */
      case 'link':
        return (
          <span key={k} role="gridcell" className={styles.link}>
            {robot === null ? (
              <span className={styles.linkUnknown}>{NO_VALUE}</span>
            ) : (
              <StateLabel
                tone={LIVENESS_STATE[robot.liveness].tone}
                hollow={LIVENESS_STATE[robot.liveness].hollow}
                mono={false}
                label={LIVENESS_STATE[robot.liveness].label}
                loud={robot.liveness === 'offline'}
                title={`${LIVENESS_STATE[robot.liveness].hint}${
                  robot.last_seen === null ? ' — never seen' : ` · last seen ${relative(robot.last_seen, now)}`
                }`}
              />
            )}
          </span>
        );
      case 'verb':
        return (
          <span key={k} role="gridcell" className={styles.verb}>{cmd.command === '' ? NO_VALUE : cmd.command}</span>
        );
      case 'stage':
        return <LifecycleTrack key={k} status={cmd.status} cell />;
      case 'detail':
        return (
          <span
            key={k}
            role="gridcell"
            className={styles.detail}
            title={
              detail.text === '' && detail.mark === null
                ? 'Nothing recorded for this command — no parameters, no result, no reason'
                : `${detail.mark === null ? '' : `${detail.mark} — `}${detail.text}`
            }
          >
            {detail.mark !== null && <span className={styles.detailCode}>{detail.mark}</span>}
            {detail.text === '' && detail.mark === null ? (
              <span className={styles.noDetail}>{NO_VALUE}</span>
            ) : (
              <span className={styles.detailText}>{detail.text}</span>
            )}
          </span>
        );
      case 'issuer':
        return (
          <span key={k} role="gridcell" className={styles.issuer} title={cmd.issued_by}>{cmd.issued_by}</span>
        );
      case 'issued':
        return (
          <span key={k} role="gridcell" className={styles.num} title={utc(cmd.issued_at)}>
            {utcTime(cmd.issued_at)}
          </span>
        );
      case 'latency':
        return (
          <BandedAge key={k} role="gridcell" ms={lat} band={latBand} note={bandNote(latBand, ladder, 'latency')} />
        );
      case 'instate':
        return (
          <BandedAge key={k} role="gridcell" ms={inState} band={waitB} note={bandNote(waitB, ladder, 'wait')} />
        );
    }
  };

  return (
    /* A ROW IS A ROW, NOT A BUTTON. It used to be a <button>, which cannot
       legally contain the selection checkbox the action bar needs — the same
       reason `DataTable` draws its rows as a clickable `<tr>` with a real
       `<td>` for the tick. Everything the button gave it is kept explicitly:
       the roving tabindex, Enter/Space to open, and the pointer affordance. */
    <div
      role="row"
      className={styles.row}
      style={{ gridTemplateColumns: template }}
      data-tone={tone}
      data-selected={selected}
      data-picked={picked}
      data-cmd={cmd.id}
      /* THE CONSOLE'S OWN ROW CONTRACT. `shell/keyboard.ts` walks
         `[data-kbd-row]` with `j`/`k`/Home/End from anywhere on the surface,
         and this board — the one surface whose entire content is a list — was
         the only list in the build that did not answer them, so the sheet's
         whole `Lists and tables` block printed as "elsewhere" here. The rows
         already carry their own roving tabindex, which is what stops
         components/motion/widgets.ts from claiming them a second time. */
      data-kbd-row=""
      tabIndex={tabbable ? 0 : -1}
      aria-current={selected ? 'true' : undefined}
      aria-selected={picked}
      title={`#${cmd.id} ${cmd.command} → ${cmd.robot_id} · ${cmd.status} · issued ${utc(cmd.issued_at)} by ${cmd.issued_by}`}
      onClick={() => onPick(cmd)}
      onKeyDown={(e) => {
        /* The row's own keys only. Space on the selection tick inside it is
           the tick's — handling it here as well would open the record every
           time an operator added a row to the batch from the keyboard. */
        if (e.target !== e.currentTarget) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        onPick(cmd);
      }}
    >
      {cols.map((k) => cell(k))}
    </div>
  );
}

/* --------------------------------------------------------------------------
   The activity stream. Raw audit events at their true timestamps.
   -------------------------------------------------------------------------- */

function FeedRow({
  ev,
  verb,
  delta,
  payload,
  build,
  cols,
  template,
  now,
  selected,
  fresh,
  tabbable,
  onPick,
}: {
  ev: CommandEvent;
  /** The command's verb, joined from the ledger — transitions do not carry it. */
  verb: string;
  /** ms since this command's previous transition. Null on the issue event. */
  delta: number | null;
  /** The body this transition carried, split into fault class and text. */
  payload: { code: string | null; text: string };
  /** The target's build, from the registry. Null where it has not been read. */
  build: string | null;
  cols: readonly FeedKey[];
  template: string;
  now: number;
  selected: boolean;
  /** Arrived since this session opened the board, and it is a fault. */
  fresh: boolean;
  tabbable: boolean;
  onPick: () => void;
}) {
  const tone: Tone =
    ev.status === null ? 'neutral' : CMD_TONE[ev.status];
  const age = now - Date.parse(ev.ts);

  const cell = (k: FeedKey) => {
    switch (k) {
      case 'time':
        return <span key={k} className={styles.feedTime}>{utcTime(ev.ts)}</span>;
      case 'age':
        return <span key={k} className={styles.feedAge}>{duration(age)}</span>;
      case 'delta':
        return (
          <span
            key={k}
            className={styles.feedDelta}
            data-known={delta !== null}
            title={
              delta === null
                ? 'The issue event — nothing precedes it on this command'
                : `${duration(delta)} after this command's previous transition`
            }
          >
            {delta === null ? NO_VALUE : `+${duration(delta)}`}
          </span>
        );
      case 'id':
        return <span key={k} className={styles.feedId}>{ev.command_id}</span>;
      case 'robot':
        return <span key={k} className={styles.feedRobot}>{ev.robot_id}</span>;
      case 'build':
        return (
          <span key={k} className={styles.build} data-known={build !== null}>
            {build ?? NO_VALUE}
          </span>
        );
      case 'event':
        return (
          <span key={k} className={styles.feedEvent}>
            <StatusDot tone={tone} />
            {ev.event}
          </span>
        );
      case 'verb':
        return (
          <span key={k} className={styles.feedVerb}>
            {verb === '' ? NO_VALUE : verb}
          </span>
        );
      case 'who':
        return <span key={k} className={styles.feedWho} title={ev.operator}>{ev.operator}</span>;
      case 'payload':
        return (
          <span
            key={k}
            className={styles.feedRest}
            title={
              payload.text === '' && payload.code === null
                ? 'The platform records no body for this hop'
                : `${payload.code === null ? '' : `${payload.code} — `}${payload.text}`
            }
          >
            {payload.code !== null && <span className={styles.feedCode}>{payload.code}</span>}
            {payload.text === '' && payload.code === null ? (
              <span className={styles.noDetail}>{NO_VALUE}</span>
            ) : (
              <span className={styles.feedFail}>{payload.text}</span>
            )}
          </span>
        );
    }
  };

  return (
    <button
      type="button"
      className={styles.feedRow}
      style={{ gridTemplateColumns: template }}
      data-tone={tone}
      data-selected={selected}
      data-fresh={fresh}
      data-ev={ev.id}
      /* The log is the surface's second list, and `primaryList` walks
         whichever one the cursor is standing in. */
      data-kbd-row=""
      tabIndex={tabbable ? 0 : -1}
      aria-current={selected ? 'true' : undefined}
      title={`${utc(ev.ts)} · ${ev.event} · #${ev.command_id} · ${ev.operator}`}
      onClick={onPick}
    >
      {cols.map((k) => cell(k))}
    </button>
  );
}

/* --------------------------------------------------------------------------
   The inspector — one command, in full, beside the board rather than over it.
   -------------------------------------------------------------------------- */


/* --------------------------------------------------------------------------
   WHERE THE VIEWPORT SITS IN THE CONTENT, as two 0–1 fractions.

   This console runs under overlay scrollbars: a scroll box measures
   `offsetWidth - clientWidth === 0` and the native thumb is painted only
   while the wheel is turning. So "how much of this is off screen, and where
   am I in it" has no persistent native answer, and a pane whose only clipping
   signal is a fade reads as a broken render rather than as "scroll". These
   two numbers drive a drawn 3px track that is always there.
   -------------------------------------------------------------------------- */

interface ScrollView {
  /** Fraction of the content above the viewport's top edge. */
  top: number;
  /** Fraction of the content the viewport covers. 1 = nothing is hidden. */
  size: number;
  scrollable: boolean;
}

const NOT_SCROLLABLE: ScrollView = { top: 0, size: 1, scrollable: false };

function useScrollView<T extends HTMLElement>(): {
  ref: (node: T | null) => void;
  view: ScrollView;
} {
  const [node, setNode] = useState<T | null>(null);
  const [view, setView] = useState<ScrollView>(NOT_SCROLLABLE);
  const ref = useCallback((n: T | null) => setNode(n), []);

  useEffect(() => {
    if (node === null) {
      setView(NOT_SCROLLABLE);
      return undefined;
    }
    const measure = () => {
      const total = node.scrollHeight;
      const seen = node.clientHeight;
      setView(
        total <= seen + 1 || total === 0
          ? NOT_SCROLLABLE
          : { top: node.scrollTop / total, size: seen / total, scrollable: true },
      );
    };
    measure();
    node.addEventListener('scroll', measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    const mo = new MutationObserver(measure);
    mo.observe(node, { childList: true, subtree: true });
    return () => {
      node.removeEventListener('scroll', measure);
      ro.disconnect();
      mo.disconnect();
    };
  }, [node]);

  return { ref, view };
}

/* --------------------------------------------------------------------------
   THE RAIL'S HEADER, WHICH IS ALSO ITS SWITCH.

   Two flat, underline-marked segments in the panel's own 28px title bar
   (THE BAR §6: verb tabs, accent text + a 2px accent underline, no pills).
   They live in the header rather than on a strip above it because a 24px tab
   strip over a 28px panel header would spend 52px of a 706px rail naming the
   same pane twice.

   Each segment carries the live fact that decides whether you want it: which
   record is loaded and what state it is in, and whether a verb is sitting
   staged on the bench. Left/Right move between them.
   -------------------------------------------------------------------------- */

type RailTab = 'record' | 'dispatch';

const RAIL_TABS: readonly RailTab[] = ['record', 'dispatch'];

function RailTabs({
  active,
  onChange,
  recordId,
  recordTone,
  staged,
  target,
}: {
  active: RailTab;
  onChange: (t: RailTab) => void;
  recordId: number | null;
  recordTone: Tone | null;
  staged: string | null;
  /** The robot the bench is pointed at. Printed on the tab so where a command
      would go is readable without switching to the pane that would send it. */
  target: string | null;
}) {
  /* Only the pane on screen renders this strip — two copies would mean two
     elements answering to one id — so activating a tab destroys the button
     that was focused and rebuilds it in the other pane. Put the focus back on
     it, or a keyboard operator switching panes lands on <body>. */
  const go = (t: RailTab) => {
    const fromTab =
      document.activeElement instanceof HTMLElement &&
      document.activeElement.getAttribute('role') === 'tab';
    onChange(t);
    if (fromTab) {
      requestAnimationFrame(() => document.getElementById(`rail-tab-${t}`)?.focus());
    }
  };

  const move = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const i = RAIL_TABS.indexOf(active);
    let next = i;
    if (e.key === 'ArrowRight') next = (i + 1) % RAIL_TABS.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + RAIL_TABS.length) % RAIL_TABS.length;
    else return;
    e.preventDefault();
    go(RAIL_TABS[next] as RailTab);
  };

  return (
    <div className={styles.railTabs} role="tablist" aria-label="Rail pane" onKeyDown={move}>
      <button
        type="button"
        role="tab"
        id="rail-tab-record"
        aria-selected={active === 'record'}
        tabIndex={active === 'record' ? 0 : -1}
        className={styles.railTab}
        title={
          recordId === null
            ? 'No command selected — pick a row on the board'
            : `Read #${recordId} — its transitions, its parameters and its result`
        }
        onClick={() => go('record')}
      >
        <span className={styles.railTabWord}>Record</span>
        <span className={styles.railTabId}>
          {recordId === null ? NO_VALUE : `#${recordId}`}
        </span>
        {/* The record's state, for when the bench has the rail and the meta
            line beside it is the bench's, not the record's. Reading the
            record, the same fact is spelled out in words two pixels to the
            right, so the dot stands down rather than saying it twice. */}
        {recordTone !== null && active !== 'record' && <StatusDot tone={recordTone} />}
      </button>
      {/* The seam between two live labels of mixed colour. Presentational, so
          the tablist still holds exactly two tabs. */}
      <span className={styles.railTabSep} role="presentation" />
      <button
        type="button"
        role="tab"
        id="rail-tab-dispatch"
        aria-selected={active === 'dispatch'}
        tabIndex={active === 'dispatch' ? 0 : -1}
        className={styles.railTab}
        data-staged={staged !== null}
        aria-label={
          staged === null
            ? `Dispatch bench${target === null ? '' : ` — target ${target}`}`
            : `Dispatch bench — ${staged} staged for ${target ?? 'no target'}, not sent`
        }
        title={
          staged === null
            ? target === null
              ? 'The dispatch bench — no target picked'
              : `The dispatch bench, pointed at ${target} — pick a verb, or press its number`
            : `${staged} is staged for ${target ?? 'no target'} and has not been sent`
        }
        onClick={() => go('dispatch')}
      >
        <span className={styles.railTabWord}>Dispatch</span>
        {/* THE TARGET IS PRINTED WHERE IT FITS, AND NOWHERE ELSE.
            A 328px header carrying two tab names, the record's time-in-state
            and a close cannot also carry an 8-character robot id, and
            `Dispatch rov` is not a truncation — it is a deletion with three
            characters left behind to prove it happened. So the idle bench's
            target rides the tab only while the bench HAS the rail (where it is
            also the first line of the pane, on its own row), and everywhere
            else the tab is the word alone with the target on its title. A
            STAGED verb always prints: that is unfinished business, and it is
            the one thing the operator must be able to see from the pane they
            are not standing in. */}
        {staged !== null ? (
          <span className={styles.railTabId} data-staged="true">{staged}</span>
        ) : (
          active === 'dispatch' && (
            <span className={styles.railTabId}>{target ?? NO_VALUE}</span>
          )
        )}
      </button>
    </div>
  );
}

function Inspector({
  tabs,
  off,
  siblings,
  stablemates,
  onPickSibling,
  cmd,
  robot,
  reissuable,
  onReissue,
  now,
  ladder,
  health: h,
  busy,
  error,
  confirming,
  onConfirm,
  onAbandon,
  onCancel,
  onClose,
}: {
  /** The rail's two-segment header, shared with the dispatch bench. */
  tabs: ReactNode;
  /** The bench has the rail. Kept mounted so the scroll position survives. */
  off: boolean;
  /** The same verb's other runs on the same robot, newest first. */
  siblings: readonly LedgerCommand[];
  /** Every OTHER verb on the same robot, newest first. */
  stablemates: readonly LedgerCommand[];
  onPickSibling: (c: LedgerCommand) => void;
  cmd: LedgerCommand | null;
  /** The target's own row, for provenance. Null while /api/robots is reading. */
  robot: Robot | null;
  /** Does the target still publish this verb? A re-issue of a verb the robot
      has dropped is a command the platform would reject, so the action says
      so rather than offering it. */
  reissuable: boolean;
  onReissue: (c: LedgerCommand) => void;
  /** The board's clock — frozen at the last good read while the feed is dead. */
  now: number;
  /** The window's threshold ladder, so the rail's age inks like its row. */
  ladder: Ladder;
  /** The freshness of the read this command was folded from. */
  health: Health;
  busy: boolean;
  error: string | null;
  confirming: boolean;
  onConfirm: () => void;
  onAbandon: () => void;
  onCancel: (c: LedgerCommand) => void;
  onClose: () => void;
}) {
  /* Which edge of the record is clipped right now. Declared before the empty
     branch returns, because a hook is not allowed to be conditional. */
  const { ref: scrollRef, edge } = useScrollEdges<HTMLDivElement>();
  const { ref: meterRef, view } = useScrollView<HTMLDivElement>();

  const cls = off ? `${styles.inspector} ${styles.paneOff}` : styles.inspector;

  if (cmd === null) {
    return (
      <Panel
        className={cls}
        title={tabs}
        meta={<span className={styles.window}>none selected</span>}
      >
        <Empty prompt>Pick a row to read its transitions, params and result.</Empty>
      </Panel>
    );
  }

  const tone = CMD_TONE[cmd.status];
  const fail = failureOf(cmd.result);
  const paramKeys = Object.keys(cmd.params);
  const resultKeys = cmd.result === null ? [] : Object.keys(cmd.result).filter((k) => k !== 'error' && k !== 'code');

  /* Verb and robot are the two halves of one fact — WHAT went WHERE — and they
     are the record's NAME, so they are set as one 13px mono line directly
     under the trace rather than as two rows of a label/value grid. Together
     with the state sentence beneath, that is a sub-60px identity block (THE
     BAR §6) where six stacked grid rows used to cost 126px — which on a
     1280x800 rail was 126px taken off the scrolling tail. What is left in the
     grid is the provenance nobody can infer: who issued it, and when. */
  const facts: KVItem[] = [
    { label: 'Issued by', value: cmd.issued_by },
    { label: 'Issued', value: utc(cmd.issued_at), title: `${duration(now - Date.parse(cmd.issued_at))} ago` },
    { label: 'Updated', value: utc(cmd.updated_at), title: `${duration(now - Date.parse(cmd.updated_at))} ago` },
  ];

  /* §13 tell 23 — provenance that stops at the robot name is not provenance.
     Which machine, which shape of machine, which firmware ran it, and whether
     that machine is reachable at all right now. Everything off /api/robots;
     a field the row does not carry renders as the em dash, never as a guess. */
  const target: KVItem[] =
    robot === null
      ? [{ label: 'Model', value: NO_VALUE }]
      : [
          { label: 'Model', value: `${robot.morphology} · ${robot.model}` },
          {
            label: 'Build',
            value: robot.firmware ?? NO_VALUE,
            title: 'The build the target published on its last check-in — the same string the board\'s Build column carries. It is the build running NOW, not necessarily the one that ran this command.',
          },
          {
            label: 'Site',
            value: siteOf(robot) === null
              ? NO_VALUE
              : typeof robot.meta['bay'] === 'string'
                ? `${siteOf(robot) ?? ''} · ${robot.meta['bay']}`
                : (siteOf(robot) ?? NO_VALUE),
          },
          {
            label: 'Battery',
            value: robot.battery_pct === null ? NO_VALUE : `${robot.battery_pct.toFixed(0)} %`,
            title: 'The target\'s charge at the last read. A robot below its reserve rejects motion.',
          },
          {
            label: 'Link',
            mono: false,
            value: (
              <span className={styles.linkCell}>
                <StateLabel
                  tone={LIVENESS_STATE[robot.liveness].tone}
                  hollow={LIVENESS_STATE[robot.liveness].hollow}
                  mono={false}
                  label={LIVENESS_STATE[robot.liveness].label}
                  title={LIVENESS_STATE[robot.liveness].hint}
                />
                <span className={styles.linkAge}>
                  {robot.last_seen === null ? 'never seen' : relative(robot.last_seen, now)}
                </span>
              </span>
            ),
          },
        ];

  const cancellable = IN_FLIGHT.has(cmd.status);
  const inspectBand = waitBand(now - Date.parse(cmd.updated_at), cmd.status, ladder);

  /* What is actually below the fold, counted. The pinned head above holds the
     identity; everything under the DETAIL rule is one scrolling list and the
     operator is entitled to know how long it is before scrolling it. */
  const tailRows =
    target.length + cmd.events.length + paramKeys.length + resultKeys.length +
    siblings.length + stablemates.length;

  return (
    <Panel
      className={cls}
      title={tabs}
      /* The state word moved into the pinned identity grid below, where the
         rest of the record's identity lives; the header keeps the one fact
         that is not written anywhere else on this pane — how long it has been
         in that state, inked against the board's own ladder. Two tabs, a
         banded age and a close is what a 328px header holds. */
      meta={
        <>
          <span className={styles.metaKey}>in state</span>
          <span className={styles.age} data-stale={!h.live}>
            <BandedAge
              ms={now - Date.parse(cmd.updated_at)}
              band={inspectBand}
              note={
                h.live
                  ? `Time in this state. ${bandNote(inspectBand, ladder, 'wait')}`
                  : `Time in this state at the last good read of ${h.worst.endpoint} — it is not advancing`
              }
            />
          </span>
        </>
      }
      tools={
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close the inspector" title="Close">
          <Icon name="close" size={12} />
        </button>
      }
      pad="none"
      scroll={false}
    >
      <div className={styles.inspectFrame} role="tabpanel" aria-labelledby="rail-tab-record">
        {/* ---- PINNED: WHAT THIS RECORD IS -------------------------------
            The trace, the state's own sentence, the failure that caused the
            row to be opened, and the five identity facts. Six lines, ~150px,
            and they never scroll: the operator clicked this row to read
            exactly these, and a record whose FAILURE line is behind a scroll
            is a record that answered the wrong question. Everything that
            grows with the command — its transitions, its parameters, its
            result — is below the rule and scrolls. */}
        <div className={styles.inspectHead}>
          <TransitionTrace cmd={cmd} now={now} />

          <div className={styles.ident}>
            <span className={styles.identVerb}>{cmd.command === '' ? NO_VALUE : cmd.command}</span>
            <span className={styles.identArrow} aria-hidden="true">→</span>
            <RobotLink
              id={cmd.robot_id}
              title={`Open ${cmd.robot_id} — its telemetry, its whole command history, its captures`}
            />
          </div>
          <p className={styles.trackWord}>
            <StateLabel
              tone={tone}
              label={cmd.status}
              loud={cmd.status === 'failed'}
              title={CMD_HINT[cmd.status]}
            />
            <span className={styles.trackSays}>{CMD_HINT[cmd.status]}</span>
          </p>

          {fail !== null && (
            <>
              <div className={styles.blockHead} data-tone="error"><Eyebrow>Failure</Eyebrow>
                {fail.code !== null && <span className={styles.blockAside}>{fail.code}</span>}
              </div>
              <p className={styles.failure}>{fail.message}</p>
            </>
          )}

          <div className={styles.blockHead}><Eyebrow>Command</Eyebrow>
            <span className={styles.blockAside}>{cmd.live ? 'in flight queue' : 'audit log'}</span>
          </div>
          <KeyValue items={facts} layout="rows" />
        </div>

        {/* The rule that says where the pinned record stops and the scrolling
            tail starts, carrying the tail's own row count. */}
        <div className={styles.tailHead}>
          <Eyebrow>Detail</Eyebrow>
          <span className={styles.blockAside}>{tailRows} rows</span>
        </div>

        <div className={styles.inspectPane}>
        <div
          className={styles.inspectScroll}
          ref={(n) => { scrollRef(n); meterRef(n); }}
          data-edge={edge}
          data-stale={!h.live}
        >
          <div className={styles.blockHead}><Eyebrow>Target</Eyebrow>
            <span className={styles.blockAside}>{robot === null ? '' : robot.status}</span>
          </div>
          <KeyValue items={target} layout="rows" />

          <div className={styles.blockHead}><Eyebrow>Transitions</Eyebrow>
            <span className={styles.blockAside}>{cmd.events.length}</span>
          </div>
          {cmd.events.length === 0 ? (
            <p className={styles.note}>
              Outside the audit window — this row came from the in-flight queue.
            </p>
          ) : (
            <div className={styles.events}>
              {cmd.events.map((e, i) => {
                const prev = cmd.events[i - 1];
                const delta = prev === undefined ? null : Date.parse(e.ts) - Date.parse(prev.ts);
                return (
                  <div key={e.id} className={styles.event} data-tone={e.status === null ? 'neutral' : CMD_TONE[e.status]}>
                    <StatusDot tone={e.status === null ? 'neutral' : CMD_TONE[e.status]} />
                    <span className={styles.eventName}>{e.event}</span>
                    <span className={styles.eventTime}>{utc(e.ts)}</span>
                    <span className={styles.eventDelta}>{delta === null ? '' : `+${duration(delta)}`}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div className={styles.blockHead}><Eyebrow>Params</Eyebrow>
            <span className={styles.blockAside}>{paramKeys.length}</span>
          </div>
          {paramKeys.length === 0 ? (
            <p className={styles.note}>None — the verb is the whole command.</p>
          ) : (
            <KeyValue
              layout="rows"
              items={paramKeys.map((k) => ({ label: k, value: formatParam(k, cmd.params[k]) }))}
            />
          )}

          {resultKeys.length > 0 && (
            <>
              <div className={styles.blockHead}><Eyebrow>Result</Eyebrow></div>
              <KeyValue
                layout="rows"
                items={resultKeys.map((k) => ({
                  label: k,
                  value: formatParam(k, cmd.result?.[k]),
                }))}
              />
            </>
          )}

          <div className={styles.blockHead}><Eyebrow>Same verb</Eyebrow>
            <span className={styles.blockAside}>
              {cmd.command === '' ? NO_VALUE : `${cmd.command} · ${cmd.robot_id}`}
            </span>
          </div>
          {siblings.length === 0 ? (
            <p className={styles.note}>
              No other run of it on this robot in the ledger this board holds.
            </p>
          ) : (
            <div className={styles.events}>
              {siblings.map((c) => (
                <SibRow key={c.id} cmd={c} now={now} onPick={onPickSibling} />
              ))}
            </div>
          )}

          {/* WHAT ELSE IS ON THIS MACHINE. The verb history above answers "is
              this verb broken here"; this answers "is this machine busy, and
              with what" — the two questions an operator asks in that order,
              and the reason a stuck `resume` is stuck is usually in this list.
              Same ledger, no second read. */}
          <div className={styles.blockHead}><Eyebrow>Same robot</Eyebrow>
            <span className={styles.blockAside}>
              {cmd.robot_id}
              {stablemates.length > 0 ? ` · ${stablemates.length}` : ''}
            </span>
          </div>
          {stablemates.length === 0 ? (
            <p className={styles.note}>
              Nothing else addressed to {cmd.robot_id} in the ledger this board holds.
            </p>
          ) : (
            <div className={styles.events}>
              {stablemates.map((c) => (
                <SibRow key={c.id} cmd={c} now={now} verb onPick={onPickSibling} />
              ))}
            </div>
          )}

          {error !== null && <ErrorLine>{error}</ErrorLine>}
        </div>
        {/* The pane's own scrollbar. The console runs under overlay
            scrollbars, so the native thumb exists only while the wheel is
            turning; this one is always there and always says how much of the
            tail is off screen. */}
        {view.scrollable && (
          <div className={styles.scrollMeter} aria-hidden="true">
            <span
              className={styles.scrollThumb}
              style={{ top: `${view.top * 100}%`, height: `${view.size * 100}%` }}
            />
          </div>
        )}
        </div>

        {/* THE RECORD'S OWN VERB BLOCK.

            One unmistakable action on its own row, above anything that can
            destroy work (skydio-dfr-incident-dispatch.png: `Edit │ Delete`
            side by side, then the commit alone and full width beneath them).
            Here the action is RE-ISSUE, because after reading a failure the
            operator's next move is to run the thing again, and the honest
            version of that is not a second send button — it is this command,
            with its own parameters, put back on the dispatch bench behind the
            same CANCEL / SEND pair as everything else. The line under it says
            exactly what will be staged and that nothing is being sent. */}
        <div className={styles.inspectFoot}>
          <Button
            size="sm"
            /* FILLED, AND THE ONLY FILLED THING IN THIS PANE. The reference
               puts one unmistakable commit on its own row ABOVE the
               destructive verbs (skydio-dfr-incident-dispatch.png); outlined,
               it was quieter than the red Withdraw under it and the pane read
               as though destroying the command were the recommended act.
               Where the verb cannot be staged the fill goes with it — an
               accent on a dead control is an accent that means nothing. */
            variant={reissuable ? 'primary' : 'secondary'}
            block
            icon="refresh"
            disabled={!reissuable}
            title={
              reissuable
                ? `Stage ${cmd.command} for ${cmd.robot_id} with #${cmd.id}'s parameters. Nothing is sent until you commit it.`
                : robot === null
                  ? `Reading ${cmd.robot_id}'s command profile`
                  : `${cmd.robot_id} does not publish ${cmd.command === '' ? 'this verb' : cmd.command} in its profile`
            }
            onClick={() => onReissue(cmd)}
          >
            Re-issue
          </Button>
          {/* WHAT THE BUTTON WILL DO, NOT A STATUS. It read `pause → rover-01 ·
              staged, not sent` with nothing staged — a sentence in the past
              tense about a thing that has not happened, on the one surface
              where the operator has to know exactly what has and has not left
              the browser. The verb is now the sentence's verb. */}
          <p className={styles.reissueNote}>
            {reissuable ? (
              <>
                stages{' '}
                <span className={styles.reissueVerb}>{cmd.command}</span>
                <span className={styles.reissueArrow}>→</span>
                <span className={styles.reissueRobot}>{cmd.robot_id}</span>
                <span className={styles.reissueArrow}>·</span>
                {' '}nothing is sent
              </>
            ) : robot === null ? (
              `reading ${cmd.robot_id}'s profile`
            ) : (
              `${cmd.robot_id} no longer publishes ${cmd.command === '' ? 'this verb' : cmd.command}`
            )}
          </p>
          {!cancellable ? (
            <span className={styles.footNote}>
              {TERMINAL.has(cmd.status) ? 'Settled — nothing to withdraw.' : ''}
            </span>
          ) : confirming ? (
            <div className={styles.confirmPair}>
              <Button size="sm" variant="secondary" block onClick={onAbandon}>Keep</Button>
              <Button size="sm" variant="danger" block disabled={busy} onClick={() => onCancel(cmd)}>
                Cancel #{cmd.id}
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="danger" block icon="x-circle" onClick={onConfirm}>
              Withdraw command
            </Button>
          )}
        </div>
      </div>
    </Panel>
  );
}

/* --------------------------------------------------------------------------
   One line of the record's two precedent lists. Identical geometry in both,
   so the eye reads them as one ledger split by a heading rather than as two
   different widgets; the verb is printed only where the block is not already
   named for one.
   -------------------------------------------------------------------------- */

function SibRow({
  cmd,
  now,
  verb,
  onPick,
}: {
  cmd: LedgerCommand;
  now: number;
  /** Print the verb — true in the `same robot` block, where it varies. */
  verb?: boolean | undefined;
  onPick: (c: LedgerCommand) => void;
}) {
  const ms = latencyMs(cmd);
  const sf = failureOf(cmd.result);
  return (
    <button
      type="button"
      className={styles.sibling}
      data-tone={CMD_TONE[cmd.status]}
      data-verb={verb === true}
      title={`Open #${cmd.id} — ${cmd.command} ${cmd.status}, issued ${utc(cmd.issued_at)}`}
      onClick={() => onPick(cmd)}
    >
      <StatusDot tone={CMD_TONE[cmd.status]} />
      <span className={styles.sibId}>#{cmd.id}</span>
      {verb === true && <span className={styles.sibVerb}>{cmd.command}</span>}
      <span className={styles.sibState}>{cmd.status}</span>
      <span className={styles.sibAge}>{relative(cmd.issued_at, now)}</span>
      <span className={styles.sibCost}>
        {sf?.code ?? (ms === null ? NO_VALUE : latencyText(ms))}
      </span>
    </button>
  );
}

/* --------------------------------------------------------------------------
   One facet: Gotham's label / count / bar rows, with a folded tail.
   -------------------------------------------------------------------------- */

function FacetSection({
  label,
  items,
  cap,
  selected,
  onSelect,
  expanded,
  onToggle,
  read,
}: {
  label: string;
  items: readonly DistItem[];
  /** The read the facet was tallied from — `VERB 0` is a tally. */
  read: Read | readonly Read[];
  cap: number;
  selected: string | null;
  onSelect: (key: string) => void;
  expanded: boolean;
  onToggle: () => void;
}) {
  const folded = !expanded && items.length > cap;
  let shown = folded ? items.slice(0, cap) : items;
  /* A facet that is currently filtering the board is never in the tail: the
     rail has to show what it is doing to the rows beside it. */
  if (folded && selected !== null && !shown.some((i) => i.key === selected)) {
    const active = items.find((i) => i.key === selected);
    if (active !== undefined) shown = [...shown.slice(0, cap - 1), active];
  }
  const hidden = items.length - shown.length;

  return (
    <Section label={label} aside={<span><Figure of={read} value={items.length} /></span>}>
      <Distribution items={shown} selected={selected} onSelect={onSelect} barWidth={FACET_BAR} />
      {(hidden > 0 || expanded) && (
        <button type="button" className={styles.facetMore} aria-expanded={expanded} onClick={onToggle}>
          {expanded ? `fewer · top ${cap}` : `+${hidden} more`}
        </button>
      )}
    </Section>
  );
}

/* One `LABEL value` readout in the toolbar's right-hand cluster. */
function Readout({
  label,
  value,
  tone,
  title,
}: {
  label: string;
  value: string;
  tone?: Tone | undefined;
  title?: string | undefined;
}) {
  return (
    <span className={styles.readout} data-tone={tone} data-toned={tone !== undefined} title={title}>
      <span className={styles.readoutValue}>{value}</span>
      <span className={styles.readoutLabel}>{label}</span>
    </span>
  );
}
